// ✅ 완성된 AddContents.tsx (상대 좌표 기반)

import React, { useRef, useState, useEffect } from 'react';
// axios import 제거
import {
  Box, Typography, TextField, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, IconButton, CircularProgress, Tabs, Tab, Card, CardMedia, Grid,
  FormControlLabel, Checkbox, Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Slider from '@mui/material/Slider';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import Popover from '@mui/material/Popover';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Sticker from '../../components/Sticker';
import { KeywordSelector } from '../../components/KeywordSelector';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { uploadToR2 } from '../../utils/r2Upload';
import { useNavigate, useParams } from 'react-router-dom';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

// 프레임 변환 유틸리티 함수
const frameToTime = (frame: number, fps: number): number => {
  return frame / fps;
};

const timeToFrame = (time: number, fps: number): number => {
  return Math.round(time * fps);
};

const formatFrameTime = (frame: number, fps: number): string => {
  const time = frameToTime(frame, fps);
  return `프레임 ${frame} (${time.toFixed(2)}초)`;
};

const formatFrameRange = (startFrame: number, endFrame: number, fps: number): string => {
  const startTime = frameToTime(startFrame, fps);
  const endTime = frameToTime(endFrame, fps);
  return `프레임 ${startFrame}~${endFrame} (${startTime.toFixed(2)}~${endTime.toFixed(2)}초)`;
};

// 상품 타입 정의 수정
interface Product {
  product_id: number;
  product_name: string;
  price: number;
  main_images: string[];
  keywords: string[];
  description: string;
  store_name: string;
  is_d2c?: boolean;
  external_url?: string;
}

const mockProducts = [
  {
    id: 1,
    brand: 'to- way leather crop jacket',
    name: '워무드마인',
    price: 25800,
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=400&q=80',
  },
  {
    id: 2,
    brand: '브랜드2',
    name: 'simple cotton shirt',
    price: 19800,
    image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80',
  },
];


// 선택 바 타입을 px 단위로 변경
interface SelectBar {
  id: number;
  startPx: number; // px 단위
  endPx: number;   // px 단위
  startFrame?: number; // 시작 프레임 (새로 추가)
  endFrame?: number;   // 끝 프레임 (새로 추가)
}

function ProductTagBox({ product, x, y, onDrag, onRemove }: any) {
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setOffset({ x: e.clientX - x, y: e.clientY - y });
    e.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragging) onDrag(product.id, e.clientX - offset.x, e.clientY - offset.y);
    };
    const handleMouseUp = () => setDragging(false);
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: x, top: y,
        width: 254, height: 121,
        bgcolor: '#fff', border: '3px solid #1976d2', borderRadius: 4,
        display: 'flex', alignItems: 'center', px: 2,
        cursor: dragging ? 'grabbing' : 'grab', zIndex: 10
      }}
      onMouseDown={handleMouseDown}
    >
      <Box sx={{ width: 80, mr: 2 }}>
        <img src={product.image} alt={product.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography fontWeight={700} fontSize={20}>{product.brand}</Typography>
        <Typography fontSize={16} color="text.secondary">{product.name}</Typography>
        <Typography fontWeight={700} fontSize={18}>{product.price.toLocaleString()}원</Typography>
      </Box>
      <IconButton size="small" onClick={() => onRemove(product.id)}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// 프레임 썸네일 추출 hook
// blob URL용 썸네일 추출 (새 업로드 시 사용)
function useVideoThumbnails(videoUrl: string | null, frameCount: number) {
  const [thumbs, setThumbs] = useState<string[]>([]);
  useEffect(() => {
    if (!videoUrl || !videoUrl.startsWith('blob:')) return;
    const video = document.createElement('video');
    video.src = videoUrl;
    video.muted = true;
    video.preload = 'auto';
    let loaded = false;
    video.addEventListener('loadeddata', async () => {
      if (loaded) return;
      loaded = true;
      const duration = video.duration;
      const canvas = document.createElement('canvas');
      canvas.width = 120;
      canvas.height = 120 * (video.videoHeight / video.videoWidth);
      const ctx = canvas.getContext('2d');
      const thumbsArr: string[] = [];
      for (let i = 0; i < frameCount; i++) {
        video.currentTime = (i / (frameCount - 1)) * duration;
        await new Promise(res => { video.onseeked = () => res(null); });
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        thumbsArr.push(canvas.toDataURL('image/jpeg'));
      }
      setThumbs(thumbsArr);
    });
  }, [videoUrl, frameCount]);
  return thumbs;
}

// remote URL용 썸네일 추출 (수정 모드)
// 1차: crossOrigin 별도 video, CORS 실패 시 2차: 메인 videoRef fallback (컬러바 대체)
function useVideoThumbnailsFromRef(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  videoUrl: string | null,
  frameCount: number,
  videoReady: boolean
) {
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    console.log('[썸네일] useVideoThumbnailsFromRef 체크:', { videoUrl: videoUrl?.substring(0, 50), isBlob: videoUrl?.startsWith('blob:'), videoReady, generated, frameCount });
    if (!videoUrl || videoUrl.startsWith('blob:') || !videoReady || generated) return;
    console.log('[썸네일] 조건 통과, remote 썸네일 생성 시작');

    let cancelled = false;

    const run = async () => {
      // 1차: crossOrigin 별도 video로 시도
      try {
        const corsVideo = document.createElement('video');
        corsVideo.crossOrigin = 'anonymous';
        corsVideo.src = videoUrl;
        corsVideo.muted = true;
        corsVideo.preload = 'auto';

        await new Promise<void>((resolve, reject) => {
          corsVideo.onloadeddata = () => resolve();
          corsVideo.onerror = () => reject(new Error('CORS video load failed'));
        });

        if (cancelled) return;
        const duration = corsVideo.duration;
        const canvas = document.createElement('canvas');
        canvas.width = 120;
        canvas.height = 120 * (corsVideo.videoHeight / corsVideo.videoWidth);
        const ctx = canvas.getContext('2d');
        const thumbsArr: string[] = [];

        for (let i = 0; i < frameCount; i++) {
          if (cancelled) return;
          corsVideo.currentTime = (i / (frameCount - 1)) * duration;
          await new Promise(res => { corsVideo.onseeked = () => res(null); });
          ctx?.drawImage(corsVideo, 0, 0, canvas.width, canvas.height);
          thumbsArr.push(canvas.toDataURL('image/jpeg'));
        }

        if (!cancelled && thumbsArr.length > 0) {
          console.log(`[썸네일] crossOrigin 방식 성공: ${thumbsArr.length}개`);
          setThumbs(thumbsArr);
          setGenerated(true);
          return;
        }
      } catch (e) {
        console.warn('[썸네일] crossOrigin 방식 실패 (CORS), 컬러바 fallback:', e);
      }

      // 2차 fallback: CORS 실패 시 시간 표시 컬러바 생성
      if (cancelled) return;
      const mainVideo = videoRef.current;
      if (!mainVideo || !mainVideo.duration) return;

      const duration = mainVideo.duration;
      const colorThumbs = Array.from({ length: frameCount }, (_, i) => {
        const c = document.createElement('canvas');
        c.width = 120; c.height = 68;
        const cx = c.getContext('2d');
        if (cx) {
          const hue = (i / frameCount) * 240;
          cx.fillStyle = `hsl(${hue}, 40%, 60%)`;
          cx.fillRect(0, 0, 120, 68);
          const sec = ((i / (frameCount - 1)) * duration).toFixed(1);
          cx.fillStyle = '#fff';
          cx.font = 'bold 14px sans-serif';
          cx.textAlign = 'center';
          cx.fillText(`${sec}s`, 60, 40);
        }
        return c.toDataURL('image/png');
      });

      if (!cancelled) {
        console.log(`[썸네일] 컬러바 fallback 생성: ${colorThumbs.length}개`);
        setThumbs(colorThumbs);
        setGenerated(true);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [videoUrl, frameCount, videoReady, generated]);

  return thumbs;
}

// ProductBalloon 타입: x, y만 상대좌표(0~1), width/height 없음
interface ProductBalloon {
  iconId: number;
  x: number; // 0~1
  y: number; // 0~1
  product: typeof mockProducts[0];
}

// BalloonBox: 첨부 이미지 스타일, 드래그, 꼬리 자동 위치
function BalloonBox({ balloon, onDrag, plusIconPos }: { balloon: ProductBalloon, onDrag: (iconId: number, relX: number, relY: number) => void, plusIconPos: { x: number, y: number } }) {
  const [dragging, setDragging] = React.useState(false);
  const offsetRef = React.useRef({ x: 0, y: 0 });
  // 고정 크기(px)
  const BALLOON_W = 320;
  const BALLOON_H = 120;
  // px 변환
  let pxX = balloon.x * 300;
  let pxY = balloon.y * 533;
  // clamp: 영상 영역 벗어나지 않게
  if (pxX < 0) pxX = 0;
  if (pxY < 0) pxY = 0;
  if (pxX > 300 - BALLOON_W) pxX = 300 - BALLOON_W;
  if (pxY > 533 - BALLOON_H) pxY = 533 - BALLOON_H;
  // 드래그
  const handleMouseDown = (e: React.MouseEvent) => {
    const offset = { x: e.clientX - pxX, y: e.clientY - pxY };
    offsetRef.current = offset;
    setDragging(true);
    e.stopPropagation();
    const handleMouseMove = (ev: MouseEvent) => {
      let xPx = ev.clientX - offsetRef.current.x;
      let yPx = ev.clientY - offsetRef.current.y;
      if (xPx < 0) xPx = 0;
      if (yPx < 0) yPx = 0;
      if (xPx > 300 - BALLOON_W) xPx = 300 - BALLOON_W;
      if (yPx > 533 - BALLOON_H) yPx = 533 - BALLOON_H;
      const relX = xPx / 300;
      const relY = yPx / 533;
      onDrag(balloon.iconId, relX, relY);
    };
    const handleMouseUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };
  // 렌더링
  return (
    <Box
      sx={{
        position: 'absolute',
        left: pxX,
        top: pxY,
        zIndex: 20,
        bgcolor: '#fff',
        borderRadius: 32,
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        width: BALLOON_W,
        height: BALLOON_H,
        p: 0,
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
        fontFamily: 'inherit',
        overflow: 'visible',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* 본문 UI */}
      <Box sx={{
        display: 'flex', alignItems: 'center', width: '100%', height: '100%', pl: 32, pr: 32, py: 16, overflow: 'hidden',
      }}>
        <Box sx={{
          width: 90, height: 90, borderRadius: 16, overflow: 'hidden', bgcolor: '#f5f5f5', mr: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <img src={balloon.product.image} alt={balloon.product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 22, mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {balloon.product.brand}
          </Typography>
          <Typography sx={{ fontSize: 16, color: '#222', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {balloon.product.name}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{balloon.product.price.toLocaleString()}원</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function AddContents() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { contentId } = useParams<{ contentId: string }>();
  const isEditMode = !!contentId;
  const [userRole, setUserRole] = useState<string>('seller');
  const isCreator = userRole === 'creator';
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  
  // 컨텐츠 타입 및 이미지 상태 추가
  const [contentType, setContentType] = useState<'video' | 'image'>('video');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [frame, setFrame] = useState<number>(0);
  const [showAddBtn, setShowAddBtn] = useState<{ x: number, y: number } | null>(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [stickerShowImage, setStickerShowImage] = useState(true); // 스티커 이미지 표시 여부
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectBars, setSelectBars] = useState<SelectBar[]>([]);
  const [barId, setBarId] = useState(1);
  const barWidth = 500; // 프레임바 width를 600px로 변경
  const frameCount = videoUrl ? Math.max(10, Math.floor((videoRef.current?.duration || 10))) : 10;
  const framePx = barWidth / frameCount;
  const [selectedBarId, setSelectedBarId] = useState<'main' | number>('main');
  // 반복재생 구간 state
  const [loopRange, setLoopRange] = useState<{start: number, end: number} | null>(null);
  // 볼륨 조절
  const [volumeAnchor, setVolumeAnchor] = useState<null | HTMLElement>(null);
  const [volume, setVolume] = useState(1);
  // 영상 FPS 상태 (기본값 30fps)
  const [videoFps, setVideoFps] = useState<number>(30);

  // 현재 프레임 상태
  const [currentFrame, setCurrentFrame] = useState<number>(0);

  // 전체 프레임 수 상태
  const [totalFrames, setTotalFrames] = useState<number>(0);

  // 비디오 로드 완료 감지 (썸네일 생성 트리거 용도)
  const [videoReady, setVideoReady] = useState(false);

  // blob URL(새 업로드)용 썸네일
  const blobThumbs = useVideoThumbnails(videoUrl, frameCount);
  // remote URL(수정 모드)용 썸네일 — 메인 videoRef에서 직접 추출
  const remoteThumbs = useVideoThumbnailsFromRef(videoRef, videoUrl, frameCount, videoReady);
  // 둘 중 유효한 쪽 사용
  const thumbs = blobThumbs.length > 0 ? blobThumbs : remoteThumbs;
  // 디버그: 렌더 시 thumbs vs frameCount 비교
  console.log('[렌더] thumbs.length:', thumbs.length, 'frameCount:', frameCount, 'blobThumbs:', blobThumbs.length, 'remoteThumbs:', remoteThumbs.length, 'videoReady:', videoReady);

  // 비디오 로드 시 전체 프레임 수 계산
  useEffect(() => {
    setVideoReady(false);
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const frames = Math.floor(video.duration * videoFps);
      setTotalFrames(frames);
      setVideoReady(true);
      console.log(`[비디오] 로드 완료: 총 ${frames}프레임 (${video.duration.toFixed(2)}초, ${videoFps}fps), readyState: ${video.readyState}`);
    };

    console.log(`[비디오] useEffect 실행, videoUrl: ${videoUrl?.substring(0, 50)}, readyState: ${video.readyState}, duration: ${video.duration}`);

    // 이미 메타데이터가 로드된 경우 (수정 모드에서 remote URL이 먼저 로드될 수 있음)
    if (video.readyState >= 1 && video.duration) {
      console.log('[비디오] 이미 메타데이터 로드됨, 즉시 처리');
      handleLoadedMetadata();
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [videoUrl, videoFps]);

  // 스티커 상태: 상품 정보 + 위치(상대좌표) + 구간 정보 (프레임 기반) + 이미지 인덱스
  const [stickers, setStickers] = useState<{
    id: number;
    product: Product;
    x: number;
    y: number;
    barId: number | 'main'; // 프레임바 id
    startFrame: number; // 시작 프레임
    endFrame: number;   // 끝 프레임
    imageIndex?: number; // 이미지 모드에서 어떤 이미지에 속하는지
    showImage?: boolean; // 스티커에서 이미지 표시 여부 (기본: true)
    isD2c?: boolean; // D2C 자사몰 연결 여부
    externalUrl?: string; // D2C 자사몰 URL
    // 하위 호환성을 위해 초 단위도 유지
    start?: number; // 시작 초 (deprecated)
    end?: number;   // 끝 초 (deprecated)
  }[]>([]);
  const [nextStickerId, setNextStickerId] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  // 역할 조회 (creator는 상품 태그 섹션 숨김)
  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('role').eq('id', user.id).single().then(({ data }) => {
      if (data?.role) setUserRole(data.role);
    });
  }, [user]);

  // Instagram Reel 링크 상태 추가
  const [instagramReelLink, setInstagramReelLink] = useState<string>('');

  // ─── Instagram 피드 Picker 상태 ───────────────────────────────────────────
  interface IgMediaItem {
    id: string;
    media_type: string;
    media_url: string;
    thumbnail_url?: string;
    shortcode: string;
  }
  const [igMediaList, setIgMediaList] = useState<IgMediaItem[]>([]);
  const [igMediaLoading, setIgMediaLoading] = useState(false);
  const [showIgPicker, setShowIgPicker] = useState(false);
  const [selectedIgMedia, setSelectedIgMedia] = useState<IgMediaItem | null>(null);

  const fetchIgMedia = async () => {
    setIgMediaLoading(true);
    try {
      if (!user) throw new Error('로그인 정보 없음');
      const { data: sellerProfile } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (!sellerProfile) throw new Error('셀러 프로필 없음');

      const { data: brandData } = await supabase
        .from('share2dm_brands')
        .select('ig_account_id, ig_access_token')
        .eq('clozet_seller_id', sellerProfile.id)
        .single();
      if (!brandData?.ig_account_id || !brandData?.ig_access_token) {
        alert('Instagram 계정이 연결되어 있지 않습니다. Share2DM에서 먼저 연결해주세요.');
        return;
      }

      const res = await fetch(
        `https://graph.facebook.com/v21.0/${brandData.ig_account_id}/media?fields=id,media_type,media_url,thumbnail_url,shortcode&limit=20&access_token=${brandData.ig_access_token}`
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);

      // VIDEO 타입만 필터링
      const videos = (data.data as IgMediaItem[]).filter(m => m.media_type === 'VIDEO');
      setIgMediaList(videos);
      setShowIgPicker(true);
    } catch (e: any) {
      alert('Instagram 피드 불러오기 실패: ' + e.message);
    } finally {
      setIgMediaLoading(false);
    }
  };

  const handleSelectIgMedia = (item: IgMediaItem) => {
    setSelectedIgMedia(item);
    setVideoUrl(item.media_url);
    setInstagramReelLink(`https://www.instagram.com/reel/${item.shortcode}/`);
    setShowIgPicker(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Description과 Keywords 상태 추가
  const [description, setDescription] = useState<string>('');
  const [keywords, setKeywords] = useState<string[]>([]);
  // 썸네일 초 및 미리보기 상태
  const [thumbnailSecond, setThumbnailSecond] = useState<number>(1.0);
  const [thumbnailSecondInput, setThumbnailSecondInput] = useState<string>('1');
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  // 드래그 상태 추가
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  
  // 상품 데이터 상태 추가
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // 상품 데이터 로드 수정
  useEffect(() => {
    const fetchProducts = async () => {
      if (!user) {
        console.error('사용자 정보를 가져올 수 없습니다.');
        return;
      }

      setLoadingProducts(true);
      try {

        // seller_profile에서 seller 정보 가져오기
        const { data: sellerProfile, error: profileError } = await supabase
          .from('seller_profile')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (profileError || !sellerProfile) {
          console.error('Seller Profile 정보를 찾을 수 없습니다:', profileError);
          return;
        }

        console.log('Seller Profile ID:', sellerProfile.id);

        // 1. 자신의 상품들 가져오기
        const { data: ownProducts, error: ownProductsError } = await supabase
          .from('products')
          .select('product_id, product_name, price, main_images, keywords, description, store_name, is_d2c, external_url')
          .eq('seller_id', sellerProfile.id)
          .eq('status', 'active');

        if (ownProductsError) {
          console.error('자신의 상품 데이터 로드 실패:', ownProductsError);
        }

        // 2. 활성화된 어필리에이트 파트너십 가져오기
        const { data: partnerships, error: partnershipsError } = await supabase
          .from('affiliate_partnerships')
          .select('id, brand_id')
          .eq('creator_id', sellerProfile.id)
          .eq('status', 'active');

        if (partnershipsError) {
          console.error('파트너십 데이터 로드 실패:', partnershipsError);
        }

        let affiliateProducts: any[] = [];
        let requestedProducts: any[] = [];

        // 3. 파트너십이 있으면, 승인된 어필리에이트 상품들 가져오기 (특정 크리에이터 타겟)
        if (partnerships && partnerships.length > 0) {
          const partnershipIds = partnerships.map(p => p.id);

          // 승인된 product_affiliate_settings 가져오기
          const { data: approvedSettings, error: settingsError } = await supabase
            .from('product_affiliate_settings')
            .select('product_id')
            .in('partnership_id', partnershipIds)
            .eq('status', 'approved');

          if (settingsError) {
            console.error('어필리에이트 설정 로드 실패:', settingsError);
          }

          if (approvedSettings && approvedSettings.length > 0) {
            const productIds = Array.from(new Set(approvedSettings.map(s => s.product_id)));

            // 해당 상품들의 정보 가져오기
            const { data: affiliateProductsData, error: affiliateError } = await supabase
              .from('products')
              .select('product_id, product_name, price, main_images, keywords, description, store_name, is_d2c, external_url')
              .in('product_id', productIds)
              .eq('status', 'active');

            if (affiliateError) {
              console.error('어필리에이트 상품 데이터 로드 실패:', affiliateError);
            } else {
              affiliateProducts = affiliateProductsData || [];
            }
          }
        }

        // 4. 마켓플레이스에서 요청 후 승인된 상품들 가져오기 (affiliate_product_requests)
        const { data: approvedRequests, error: requestsError } = await supabase
          .from('affiliate_product_requests')
          .select('product_id')
          .eq('creator_id', sellerProfile.id)
          .eq('status', 'approved');

        if (requestsError) {
          console.error('승인된 요청 데이터 로드 실패:', requestsError);
        }

        if (approvedRequests && approvedRequests.length > 0) {
          const requestedProductIds = Array.from(new Set(approvedRequests.map(r => r.product_id)));

          // 해당 상품들의 정보 가져오기
          const { data: requestedProductsData, error: requestedError } = await supabase
            .from('products')
            .select('product_id, product_name, price, main_images, keywords, description, store_name, is_d2c, external_url')
            .in('product_id', requestedProductIds)
            .eq('status', 'active');

          if (requestedError) {
            console.error('요청된 상품 데이터 로드 실패:', requestedError);
          } else {
            requestedProducts = requestedProductsData || [];
          }
        }

        // 5. 세 배열 합치기 (중복 제거)
        const allProducts = [...(ownProducts || []), ...affiliateProducts, ...requestedProducts];
        const uniqueProducts = Array.from(
          new Map(allProducts.map(p => [p.product_id, p])).values()
        );

        console.log('로드된 상품 데이터:', {
          ownProducts: ownProducts?.length || 0,
          affiliateProducts: affiliateProducts.length,
          requestedProducts: requestedProducts.length,
          total: uniqueProducts.length
        });

        setProducts(uniqueProducts);
      } catch (error) {
        console.error('상품 데이터 로드 중 오류:', error);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [user]);

  // 수정 모드: 기존 콘텐츠 데이터 로드
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  useEffect(() => {
    if (!contentId || !user) return;
    const loadExistingContent = async () => {
      setIsLoadingContent(true);
      try {
        // 1. contents 테이블에서 기존 데이터 fetch
        const { data: content, error: contentError } = await supabase
          .from('contents')
          .select('*')
          .eq('id', contentId)
          .single();

        if (contentError || !content) {
          console.error('콘텐츠 로드 실패:', contentError);
          alert('콘텐츠를 불러올 수 없습니다.');
          return;
        }

        // 2. state에 기존 데이터 세팅
        setContentType(content.content_type || 'video');
        setDescription(content.description || '');
        setKeywords(content.keywords || []);
        if (content.short_code) {
          setInstagramReelLink(`https://www.instagram.com/reel/${content.short_code}/`);
        }

        if (content.content_type === 'video' && content.video_url) {
          setVideoUrl(content.video_url); // remote URL — video 태그에서 바로 재생 가능
        }
        if (content.content_type === 'image' && content.image_urls) {
          setImageUrls(content.image_urls); // remote R2 URL 배열
        }
        if (content.thumbnail_second != null) {
          setThumbnailSecond(content.thumbnail_second);
          setThumbnailSecondInput(String(content.thumbnail_second));
        }

        // 3. 스티커(상품 태그) 데이터 로드
        console.log('[스티커로드] content_product_tags 조회 시작, contentId:', contentId);
        const { data: tags, error: tagsError } = await supabase
          .from('content_product_tags')
          .select('*')
          .eq('contents_id', contentId);

        console.log('[스티커로드] tags 결과:', tags?.length, '개, error:', tagsError);
        if (tagsError) {
          console.error('스티커 데이터 로드 실패:', tagsError);
        }

        if (tags && tags.length > 0) {
          // 태그에 연결된 상품 정보를 별도 쿼리로 가져오기
          const productIds = Array.from(new Set(tags.map((t: any) => t.product_id)));
          console.log('[스티커로드] 상품 조회 시작, productIds:', productIds);
          const { data: tagProducts, error: productsError } = await supabase
            .from('products')
            .select('product_id, product_name, price, main_images, keywords, description, store_name, is_d2c, external_url')
            .in('product_id', productIds);

          console.log('[스티커로드] 상품 결과:', tagProducts?.length, '개, error:', productsError);
          const productMap = new Map((tagProducts || []).map((p: any) => [p.product_id, p]));

          const loadedStickers = tags
            .filter((tag: any) => productMap.has(tag.product_id))
            .map((tag: any, idx: number) => ({
              id: idx + 1,
              product: productMap.get(tag.product_id) as Product,
              x: Number(tag.position_x),
              y: Number(tag.position_y),
              barId: 'main' as const,
              startFrame: timeToFrame(Number(tag.start_time), 30),
              endFrame: timeToFrame(Number(tag.end_time), 30),
              imageIndex: tag.image_index ?? 0,
              showImage: tag.show_image ?? true,
              isD2c: tag.is_d2c ?? false,
              externalUrl: tag.external_url || '',
              start: Number(tag.start_time),
              end: Number(tag.end_time),
            }));
          setStickers(loadedStickers);
          setNextStickerId(loadedStickers.length + 1);
          console.log('스티커 로드 완료:', loadedStickers.length, '개');
        }

        console.log('기존 콘텐츠 데이터 로드 완료:', content.id);
      } catch (error) {
        console.error('콘텐츠 데이터 로드 중 오류:', error);
      } finally {
        setIsLoadingContent(false);
      }
    };
    loadExistingContent();
  }, [contentId, user]);

  // 수정 모드: 비디오 로드 후 스티커 구간 정보로 selectBars 복원
  const [editBarsRestored, setEditBarsRestored] = useState(false);
  useEffect(() => {
    console.log('[selectBars복원] 체크:', { isEditMode, videoReady, editBarsRestored, contentType, stickersLen: stickers.length, videoDuration: videoRef.current?.duration });
    if (!isEditMode || !videoReady || editBarsRestored || contentType !== 'video') return;
    if (stickers.length === 0) return;
    const video = videoRef.current;
    if (!video || !video.duration) return;

    console.log('[selectBars복원] 조건 통과, 스티커 분석 시작');
    const duration = video.duration;
    const fullEndFrame = timeToFrame(duration, videoFps);
    console.log('[selectBars복원] duration:', duration, 'fullEndFrame:', fullEndFrame);
    stickers.forEach(s => {
      console.log(`[selectBars복원] 스티커 id:${s.id}, barId:${s.barId}, startFrame:${s.startFrame}, endFrame:${s.endFrame}`);
    });

    // 스티커를 (startFrame, endFrame) 기준으로 그룹화
    const rangeMap = new Map<string, { startFrame: number; endFrame: number; stickerIds: number[] }>();
    stickers.forEach(s => {
      const key = `${s.startFrame}_${s.endFrame}`;
      if (!rangeMap.has(key)) {
        rangeMap.set(key, { startFrame: s.startFrame, endFrame: s.endFrame, stickerIds: [] });
      }
      rangeMap.get(key)!.stickerIds.push(s.id);
    });

    const newBars: SelectBar[] = [];
    const stickerBarUpdates = new Map<number, number | 'main'>();
    let nextBarId = barId;

    rangeMap.forEach(({ startFrame, endFrame, stickerIds }) => {
      // 모든 스티커에 대해 selectBar 생성 (수정/삭제 가능하도록)
      const startPx = (frameToTime(startFrame, videoFps) / duration) * barWidth;
      const endPx = (frameToTime(endFrame, videoFps) / duration) * barWidth;
      newBars.push({ id: nextBarId, startPx, endPx, startFrame, endFrame });
      stickerIds.forEach(id => stickerBarUpdates.set(id, nextBarId));
      nextBarId++;
    });

    setSelectBars(newBars);
    setBarId(nextBarId);
    setStickers(prev => prev.map(s => {
      const newBarId = stickerBarUpdates.get(s.id);
      return newBarId !== undefined ? { ...s, barId: newBarId } : s;
    }));
    console.log(`[수정모드] selectBars ${newBars.length}개 복원 완료`);
    setEditBarsRestored(true);
  }, [isEditMode, videoReady, stickers.length, editBarsRestored, contentType]);

  // 상품 검색 필터링
  const filteredProducts = products.filter(product =>
    product.product_name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    product.store_name.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  // 디버깅을 위한 로그 추가
  useEffect(() => {
    console.log('전체 상품 개수:', products.length);
    console.log('검색어:', productSearchTerm);
    console.log('필터링된 상품 개수:', filteredProducts.length);
  }, [products, productSearchTerm, filteredProducts]);

  // 상품 데이터 로드

  // 상품 선택 시 새 스티커 추가 (비디오/이미지 모드 지원)
  const handleSelectProduct = (product: Product) => {
    if (contentType === 'image') {
      // 이미지 모드에서는 현재 이미지 인덱스와 함께 저장
      console.log(`- 이미지 ${currentImageIndex + 1}에 스티커 추가 { 상품명: ${product.product_name}, 상품 가격: ${product.price}, 상대 위치: (0.5, 0.5) }`);
      setStickers(list => [
        ...list,
        {
          id: nextStickerId,
          product,
          x: 0.5,
          y: 0.5,
          barId: 'main',
          startFrame: 0,
          endFrame: 1, // 이미지는 1프레임으로 설정
          imageIndex: currentImageIndex,
          showImage: stickerShowImage,
          isD2c: product.is_d2c ?? false,
          externalUrl: product.is_d2c ? (product.external_url || '') : undefined,
          start: 0,
          end: 1
        }
      ]);
      setNextStickerId(id => id + 1);
      setProductDialogOpen(false);
      return;
    }

    // 비디오 모드 (기존 로직)
    let barId: number | 'main' = 'main';
    let startFrame = 0, endFrame = 0;
    let start = 0, end = 0; // 하위 호환성을 위해 유지
    
    if (selectedBarId && selectedBarId !== 'main') {
      barId = selectedBarId;
      const bar = selectBars.find(b => b.id === barId);
      if (bar && videoRef.current && videoRef.current.duration > 0) {
        const duration = videoRef.current.duration;
        const barWidth = 500;
        start = (bar.startPx / barWidth) * duration;
        end = (bar.endPx / barWidth) * duration;
        startFrame = timeToFrame(start, videoFps);
        endFrame = timeToFrame(end, videoFps);
        videoRef.current.currentTime = start;
        setCurrentTime(start);
        setSelectedBarId(barId);
      } else {
        alert('영상이 완전히 로드된 후에 스티커를 추가하세요.');
        return;
      }
    } else if (videoRef.current && videoRef.current.duration > 0) {
      start = 0;
      end = videoRef.current.duration;
      startFrame = 0;
      endFrame = timeToFrame(end, videoFps);
      setSelectedBarId('main');
    } else {
      alert('영상이 완전히 로드된 후에 스티커를 추가하세요.');
      return;
    }
    
    console.log(`- 프레임바 ${barId}에 스티커 추가 { 상품명: ${product.product_name}, 상품 가격: ${product.price}, 상대 위치: (0.5, 0.5), 프레임 구간: ${startFrame}~${endFrame} (시간: ${start.toFixed(2)}~${end.toFixed(2)}초) }`);
    setStickers(list => [
      ...list,
      { id: nextStickerId, product, x: 0.5, y: 0.5, barId, startFrame, endFrame, showImage: stickerShowImage, isD2c: product.is_d2c ?? false, externalUrl: product.is_d2c ? (product.external_url || '') : undefined, start, end }
    ]);
    setNextStickerId(id => id + 1);
    setProductDialogOpen(false);
  };

  // 스티커 드래그 핸들러 (비디오/이미지 모드 지원)
  const handleStickerDrag = (id: number, e: React.MouseEvent) => {
    let containerRef: React.RefObject<HTMLVideoElement | HTMLDivElement>;
    let containerWidth: number;
    let containerHeight: number;
    
    if (contentType === 'video') {
      if (!videoRef.current) return;
      containerRef = videoRef;
      const rect = videoRef.current.getBoundingClientRect();
      containerWidth = rect.width;
      containerHeight = rect.height;
    } else {
      if (!imageContainerRef.current) return;
      containerRef = imageContainerRef;
      const rect = imageContainerRef.current.getBoundingClientRect();
      containerWidth = rect.width;
      containerHeight = rect.height;
    }
    
    const k = 0.46 * containerWidth;
    const stickerHeight = 0.1 * containerHeight;
    const sticker = stickers.find(s => s.id === id);
    if (!sticker) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const stickerCenterX = sticker.x * containerWidth;
    const stickerCenterY = sticker.y * containerHeight;
    const offset = { x: startX - stickerCenterX, y: startY - stickerCenterY };
    
    const onMove = (ev: MouseEvent) => {
      let xPx = ev.clientX - offset.x;
      let yPx = ev.clientY - offset.y;
      // clamp (스티커 중앙이 컨테이너 밖으로 안 나가게)
      if (xPx < k / 2) xPx = k / 2;
      if (yPx < stickerHeight / 2) yPx = stickerHeight / 2;
      if (xPx > containerWidth - k / 2) xPx = containerWidth - k / 2;
      if (yPx > containerHeight - stickerHeight / 2) yPx = containerHeight - stickerHeight / 2;
      setStickers(list => list.map(s =>
        s.id === id ? { ...s, x: xPx / containerWidth, y: yPx / containerHeight } : s
      ));
    };
    
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // 스티커 삭제
  const handleRemoveSticker = (id: number) => {
    const sticker = stickers.find(s => s.id === id);
    if (sticker) {
      console.log(`- 프레임바 ${sticker.barId}의 스티커 삭제 , {상품명: ${sticker.product.product_name}, 상품 가격: ${sticker.product.price}, 상대 위치: (${sticker.x.toFixed(2)}, ${sticker.y.toFixed(2)}), 프레임 구간: ${sticker.startFrame}~${sticker.endFrame}}`);
    }
    setStickers(list => list.filter(s => s.id !== id));
  };

  // 영상 play/pause 제어
  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      console.log('- 영상 일시정지');
    } else {
      videoRef.current.play();
      setIsPlaying(true);
      console.log('- 영상 재생');
    }
  };
  // 영상 상태 동기화
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  // 프레임 이동 시 영상 위치도 항상 강제로 이동
  useEffect(() => {
    if (!videoRef.current) return;
    const duration = videoRef.current.duration;
    if (!duration || isNaN(duration)) return;
    const frameCount = thumbs.length;
    videoRef.current.currentTime = (frame / (frameCount - 1)) * duration;
  }, [frame, videoUrl, thumbs.length]);

  // 영역 선택 바 추가 (초기값: 전체 바의 20~50% 구간)
  const handleAddSelectBar = () => {
    setSelectBars(bars => {
      const newBar = { id: barId, startPx: barWidth * 0.2, endPx: barWidth * 0.5 };
      console.log(`- 프레임바 추가 ! (id: ${barId})`);
      return [...bars, newBar];
    });
    setBarId(id => id + 1);
  };
  // 드래그 핸들 이동 (px 단위)
  // ★ frame이 바뀌지 않아도 강제로 미리보기 이동
  const forcePreviewFrame = (targetFrame: number) => {
    setFrame(prev => {
      if (prev === targetFrame) {
        setTimeout(() => {
          if (videoRef.current) {
            const duration = videoRef.current.duration;
            if (duration && !isNaN(duration)) {
              const frameCount = thumbs.length;
              videoRef.current.currentTime = (targetFrame / (frameCount - 1)) * duration;
            }
          }
        }, 0);
      }
      return targetFrame;
    });
  };
  const handleBarChange = (id: number, which: 'startPx' | 'endPx', value: number, updatePreview: boolean = true) => {
    setSelectBars(bars => {
      const nextBars = bars.map(bar =>
        bar.id === id ? { ...bar, [which]: Math.max(0, Math.min(barWidth, value)) } : bar
      );
      // 로그 추가: 프레임바 구간 변경
      const changedBar = nextBars.find(bar => bar.id === id);
      if (changedBar && videoRef.current) {
        const duration = videoRef.current.duration;
        const start = (changedBar.startPx / barWidth) * duration;
        const end = (changedBar.endPx / barWidth) * duration;
        console.log(`- 프레임바 ${id} 구간 변경: ${start.toFixed(2)}초 ~ ${end.toFixed(2)}초`);
        // 프레임바에 속한 스티커들의 구간도 함께 업데이트
        setStickers(list =>
          list.map(s =>
            s.barId === id
              ? { ...s, start: Math.min(start, end), end: Math.max(start, end) }
              : s
          )
        );
      }
      if (updatePreview && selectedBarId === id) {
        const bar = nextBars.find(b => b.id === id);
        if (bar) {
          const startRatio = bar.startPx / barWidth;
          const startFrame = Math.floor(startRatio * thumbs.length);
          forcePreviewFrame(startFrame);
        }
      }
      return nextBars;
    });
  };

  // 영상 반복재생 로직
  useEffect(() => {
    if (!videoRef.current || !loopRange || selectedBarId === 'main') return;
    const video = videoRef.current;
    const duration = video.duration;
    if (!duration || isNaN(duration)) return;
    const frameCount = thumbs.length;
    const startTime = (loopRange.start / frameCount) * duration;
    const endTime = (loopRange.end / frameCount) * duration;
    const onTimeUpdate = () => {
      if (video.currentTime < startTime || video.currentTime > endTime) {
        video.currentTime = startTime;
        setTimeout(() => {
          if (video.paused && isPlaying) video.play().catch(() => {});
        }, 10);
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [loopRange, selectedBarId, thumbs.length, isPlaying]);

  // 선택된 SelectBar의 첫 프레임을 미리보기로 반영
  useEffect(() => {
    if (selectedBarId === 'main') return;
    if (typeof selectedBarId !== 'number') return;
    const bar = selectBars.find(b => b.id === selectedBarId);
    if (!bar) return;
    const startRatio = bar.startPx / barWidth;
    const startFrame = Math.floor(startRatio * frameCount);
    setFrame(startFrame);
  }, [selectBars, selectedBarId, barWidth, frameCount]);

  // 키보드로 프레임 이동 및 선택된 SelectBar 이동
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 입력 필드에 포커스가 있을 때는 키보드 이벤트 무시
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }
      
      // 메인 프레임바에서 방향키로 1프레임씩 이동
      if (selectedBarId === 'main') {
        if (e.key === 'ArrowLeft') {
          const currentFrameFromTimeLocal = timeToFrame(currentTime, videoFps);
          const newFrame = Math.max(0, currentFrameFromTimeLocal - 1);
          const newTime = frameToTime(newFrame, videoFps);
          if (videoRef.current) {
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
            // 썸네일 프레임바 인덱스도 업데이트
            const thumbnailIndex = Math.floor((newTime / (videoRef.current.duration || 1)) * frameCount);
            setFrame(thumbnailIndex);
          }
          console.log(`- 방향키 ←: 프레임 ${newFrame} (${newTime.toFixed(3)}초)`);
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          const currentFrameFromTimeLocal = timeToFrame(currentTime, videoFps);
          const newFrame = Math.min(totalFrames - 1, currentFrameFromTimeLocal + 1);
          const newTime = frameToTime(newFrame, videoFps);
          if (videoRef.current) {
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
            // 썸네일 프레임바 인덱스도 업데이트
            const thumbnailIndex = Math.floor((newTime / (videoRef.current.duration || 1)) * frameCount);
            setFrame(thumbnailIndex);
          }
          console.log(`- 방향키 →: 프레임 ${newFrame} (${newTime.toFixed(3)}초)`);
          e.preventDefault();
        }
        return;
      }
      
      // 선택된 프레임바 1프레임 단위 이동
      if (typeof selectedBarId !== 'number') return;
      const barIdx = selectBars.findIndex(b => b.id === selectedBarId);
      if (barIdx === -1) return;
      const bar = selectBars[barIdx];
      
      if (e.key === 'ArrowLeft') {
        // 1프레임만큼 좌측으로 이동
        const oneFrameInPx = (1 / videoFps) * (barWidth / (videoRef.current?.duration || 1));
        const diff = Math.max(-bar.startPx, -oneFrameInPx);
        setSelectBars(bars => {
          const nextBars = bars.map(b =>
            b.id === selectedBarId
              ? { ...b, startPx: b.startPx + diff, endPx: b.endPx + diff }
              : b
          );
          const movedBar = nextBars.find(b => b.id === selectedBarId);
          if (movedBar && videoRef.current) {
            const startTime = (movedBar.startPx / barWidth) * videoRef.current.duration;
            const startFrameNum = timeToFrame(startTime, videoFps);
            videoRef.current.currentTime = startTime;
            setCurrentTime(startTime);
            console.log(`- 프레임바 ${selectedBarId} 좌측 이동: 프레임 ${startFrameNum} (${startTime.toFixed(3)}초)`);
          }
          return nextBars;
        });
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        // 1프레임만큼 우측으로 이동
        const oneFrameInPx = (1 / videoFps) * (barWidth / (videoRef.current?.duration || 1));
        const diff = Math.min(barWidth - bar.endPx, oneFrameInPx);
        setSelectBars(bars => {
          const nextBars = bars.map(b =>
            b.id === selectedBarId
              ? { ...b, startPx: b.startPx + diff, endPx: b.endPx + diff }
              : b
          );
          const movedBar = nextBars.find(b => b.id === selectedBarId);
          if (movedBar && videoRef.current) {
            const startTime = (movedBar.startPx / barWidth) * videoRef.current.duration;
            const startFrameNum = timeToFrame(startTime, videoFps);
            videoRef.current.currentTime = startTime;
            setCurrentTime(startTime);
            console.log(`- 프레임바 ${selectedBarId} 우측 이동: 프레임 ${startFrameNum} (${startTime.toFixed(3)}초)`);
          }
          return nextBars;
        });
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBarId, selectBars, framePx, barWidth, thumbs.length, currentTime, totalFrames, videoFps, frameCount]);

  // 프레임바 삭제 함수
  const handleDeleteBar = (id: number) => {
    console.log(`- 프레임바 삭제 ! (id: ${id})`);

    // 해당 프레임바에 속한 스티커들도 함께 삭제
    const deletedStickers = stickers.filter(sticker => sticker.barId === id);
    if (deletedStickers.length > 0) {
      console.log(`- 프레임바 ${id}에 속한 스티커 ${deletedStickers.length}개도 함께 삭제`);
      deletedStickers.forEach(sticker => {
        console.log(`  삭제된 스티커: {상품명: ${sticker.product.product_name}, 상품 가격: ${sticker.product.price}, 상대 위치: (${sticker.x.toFixed(2)}, ${sticker.y.toFixed(2)}), 프레임 구간: ${sticker.startFrame}~${sticker.endFrame}}`);
      });
    }
    // 프레임바 삭제
    setSelectBars(bars => bars.filter(bar => bar.id !== id));
    
    // 해당 프레임바에 속한 스티커들 삭제
    setStickers(stickers => stickers.filter(sticker => sticker.barId !== id));

    // 선택된 바가 삭제된 경우, main으로 선택 변경
    setSelectedBarId(prev => (prev === id ? 'main' : prev));
  };

  // 프레임바 재생 함수
  const handlePlayBar = (bar: SelectBar) => {
    const startRatio = bar.startPx / barWidth;
    const endRatio = bar.endPx / barWidth;
    const startFrame = Math.floor(startRatio * frameCount);
    const endFrame = Math.ceil(endRatio * frameCount);
    setSelectedBarId(bar.id);
    setLoopRange({start: startFrame, end: endFrame});
    forcePreviewFrame(startFrame);
    if (!isPlaying) handlePlayPause();
  };

  // 비디오 해상도 체크 함수
  const checkVideoResolution = (file: File): Promise<{width: number, height: number}> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight
        });
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        reject(new Error('비디오 파일을 읽을 수 없습니다.'));
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  // 이미지 크기 체크 함수
  const checkImageSize = (file: File): Promise<{width: number, height: number}> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
        URL.revokeObjectURL(img.src);
      };
      
      img.onerror = () => {
        reject(new Error('이미지 파일을 읽을 수 없습니다.'));
        URL.revokeObjectURL(img.src);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // 영상 파일 업로드 핸들러 (1080p 제한 추가)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      try {
        // 해상도 체크
        const { width, height } = await checkVideoResolution(file);
        console.log(`비디오 해상도: ${width}x${height}`);
        
        // 1280p 초과 체크 (가로/세로 모두 고려: 720x1280 또는 1280x720)
        if (width > 1280 || height > 1280) {
          alert(`비디오 해상도가 너무 높습니다.\n현재: ${width}x${height}\n최대 지원: 1280x1280\n(세로 영상: 720x1280, 가로 영상: 1280x720)\n\n더 낮은 해상도의 비디오를 사용해주세요.`);
          e.target.value = ''; // 파일 선택 초기화
          return;
        }
        
        setVideoUrl(URL.createObjectURL(file));
        console.log(`✅ 영상 업로드: ${file.name} (${width}x${height})`);
      } catch (error) {
        console.error('비디오 해상도 체크 오류:', error);
        alert('비디오 파일을 처리하는 중 오류가 발생했습니다.');
        e.target.value = '';
      }
    }
  };

  // 이미지 파일 업로드 핸들러 (크기 제한 추가)
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // 이미지 파일만 필터링
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    // 최대 10개 제한
    if (imageFiles.length > 10) {
      alert('최대 10개의 이미지만 업로드 가능합니다.');
      return;
    }

    try {
      // 각 이미지의 크기 체크
      for (const file of imageFiles) {
        const { width, height } = await checkImageSize(file);
        console.log(`이미지 크기: ${file.name} - ${width}x${height}`);
        
        // 높이 17000픽셀 초과 체크
        if (height > 17000) {
          alert(`이미지 높이가 너무 큽니다.\n파일: ${file.name}\n현재: ${width}x${height}\n최대 지원: 높이 17000픽셀\n\n더 작은 이미지를 사용해주세요.`);
          e.target.value = ''; // 파일 선택 초기화
          return;
        }
      }

      setImageFiles(imageFiles);
      
      // 이미지 미리보기 URL 생성
      const urls = imageFiles.map(file => URL.createObjectURL(file));
      setImageUrls(urls);
      setCurrentImageIndex(0);
      
      console.log(`✅ 이미지 업로드: ${imageFiles.length}개 파일 (크기 검증 완료)`);
    } catch (error) {
      console.error('이미지 크기 체크 오류:', error);
      alert('이미지 파일을 처리하는 중 오류가 발생했습니다.');
      e.target.value = '';
    }
  };

  // 이미지 순서 바꾸기 함수들
  const moveImage = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    
    const newImageFiles = [...imageFiles];
    const newImageUrls = [...imageUrls];
    
    // 배열에서 요소 이동
    const [movedFile] = newImageFiles.splice(fromIndex, 1);
    const [movedUrl] = newImageUrls.splice(fromIndex, 1);
    
    newImageFiles.splice(toIndex, 0, movedFile);
    newImageUrls.splice(toIndex, 0, movedUrl);
    
    setImageFiles(newImageFiles);
    setImageUrls(newImageUrls);
    
    // 스티커의 imageIndex도 함께 업데이트
    setStickers(prevStickers => prevStickers.map(sticker => {
      if (sticker.imageIndex === undefined) return sticker; // 비디오 스티커는 건드리지 않음
      
      let newImageIndex = sticker.imageIndex;
      if (sticker.imageIndex === fromIndex) {
        // 이동된 이미지의 스티커
        newImageIndex = toIndex;
      } else if (fromIndex < toIndex) {
        // fromIndex보다 크고 toIndex보다 작거나 같은 인덱스들은 -1
        if (sticker.imageIndex > fromIndex && sticker.imageIndex <= toIndex) {
          newImageIndex = sticker.imageIndex - 1;
        }
      } else {
        // fromIndex보다 작고 toIndex보다 크거나 같은 인덱스들은 +1
        if (sticker.imageIndex < fromIndex && sticker.imageIndex >= toIndex) {
          newImageIndex = sticker.imageIndex + 1;
        }
      }
      
      return { ...sticker, imageIndex: newImageIndex };
    }));
    
    // 현재 보고 있는 인덱스 조정
    if (currentImageIndex === fromIndex) {
      setCurrentImageIndex(toIndex);
    } else if (fromIndex < currentImageIndex && toIndex >= currentImageIndex) {
      setCurrentImageIndex(currentImageIndex - 1);
    } else if (fromIndex > currentImageIndex && toIndex <= currentImageIndex) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
    
    console.log(`이미지 순서 변경: ${fromIndex} → ${toIndex}`);
  };

  const removeImage = (index: number) => {
    const newImageFiles = imageFiles.filter((_, i) => i !== index);
    const newImageUrls = imageUrls.filter((_, i) => i !== index);
    
    setImageFiles(newImageFiles);
    setImageUrls(newImageUrls);
    
    // 삭제된 이미지의 스티커 제거 및 인덱스 조정
    setStickers(prevStickers => prevStickers
      .filter(sticker => sticker.imageIndex !== index) // 삭제된 이미지의 스티커 제거
      .map(sticker => {
        if (sticker.imageIndex === undefined) return sticker; // 비디오 스티커는 건드리지 않음
        if (sticker.imageIndex > index) {
          // 삭제된 인덱스보다 큰 스티커들은 인덱스 -1
          return { ...sticker, imageIndex: sticker.imageIndex - 1 };
        }
        return sticker;
      })
    );
    
    // 현재 인덱스 조정
    if (index < currentImageIndex) {
      setCurrentImageIndex(currentImageIndex - 1);
    } else if (index === currentImageIndex && currentImageIndex >= newImageUrls.length) {
      setCurrentImageIndex(Math.max(0, newImageUrls.length - 1));
    }
    
    console.log(`이미지 삭제: ${index}, 남은 개수: ${newImageFiles.length}`);
  };

  // 영상 시간 추적
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const onTimeUpdate = () => {
      // 드래그 중일 때는 timeupdate 이벤트 무시
      if (isDraggingPlayhead) return;
      setCurrentTime(video.currentTime);
    };
    
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [isDraggingPlayhead]);

  // visibleStickers 계산: 정확한 프레임 범위로 겹침 방지
  const currentFrameFromTime = timeToFrame(currentTime, videoFps);
  const visibleStickers = stickers.filter((sticker) => {
    if (selectedBarId === 'main') {
      // 오차 없이 정확한 범위만 표시
      return sticker.startFrame <= currentFrameFromTime && currentFrameFromTime <= sticker.endFrame;
    } else {
      return sticker.barId === selectedBarId && sticker.startFrame <= currentFrameFromTime && currentFrameFromTime <= sticker.endFrame;
    }
  });
  
  // 디버그: 스티커 겹침 확인
  if (visibleStickers.length > 1) {
    console.warn(`⚠️ 프레임 ${currentFrameFromTime}에서 스티커 겹침! (${visibleStickers.length}개)`);
    visibleStickers.forEach(sticker => {
      console.warn(`  - ID ${sticker.id}: 프레임 ${sticker.startFrame}~${sticker.endFrame} (상품: ${sticker.product.product_name})`);
    });
  }

  // R2 업로드 함수 수정 (함수명 변경)
  const uploadVideoToR2 = async (file: File, contentId?: string): Promise<string> => {
    try {
      console.log('R2 업로드 시작...');
      
      if (contentId) {
        // contentId가 있으면 uploadVideo 함수 사용
        const { uploadVideo } = await import('../../utils/r2Upload');
        const videoUrl = await uploadVideo(file, contentId);
        console.log('🔍 uploadVideo에서 반환된 URL:', videoUrl);
        return videoUrl;
      } else {
        // 임시 업로드 (contentId가 없을 때)
        const timestamp = Date.now();
        const fileName = `temp/videos/${timestamp}_${file.name}`;
        console.log('업로드할 파일명:', fileName);
        
        // utils/r2Upload.ts의 함수 사용 (import 이름을 명확히)
        const { uploadToR2, BUCKETS } = await import('../../utils/r2Upload');
        const videoUrl = await uploadToR2(file, fileName, BUCKETS.CONTENTS);
      
        console.log('🔍 uploadToR2에서 반환된 URL:', videoUrl);
        console.log('🔍 URL 타입:', typeof videoUrl);
        console.log('🔍 URL 길이:', videoUrl?.length);
        
        return videoUrl;
      }
      
    } catch (error) {
      console.error('R2 업로드 오류:', error);
      throw error;
    }
  };

  // 이미지들을 R2에 업로드하는 함수
  const uploadImagesToR2 = async (files: File[], contentId?: string): Promise<string[]> => {
    try {
      console.log('이미지 R2 업로드 시작...');
      
      if (contentId) {
        // contentId가 있으면 uploadImageContent 함수 사용
        const { uploadImageContent } = await import('../../utils/r2Upload');
        const uploadPromises = files.map(async (file, index) => {
          console.log(`업로드할 이미지 ${index}:`, file.name);
          return await uploadImageContent(file, contentId, index);
        });
        
        const imageUrls = await Promise.all(uploadPromises);
        console.log('이미지 R2 업로드 완료:', imageUrls);
        return imageUrls;
      } else {
        // 임시 업로드 (contentId가 없을 때)
        const { uploadToR2, BUCKETS } = await import('../../utils/r2Upload');
        const uploadPromises = files.map(async (file, index) => {
          const timestamp = Date.now();
          const fileName = `temp/images/${timestamp}_${index}_${file.name}`;
          console.log('업로드할 이미지 파일명:', fileName);
          return await uploadToR2(file, fileName, BUCKETS.CONTENTS);
        });
      
        const imageUrls = await Promise.all(uploadPromises);
        console.log('이미지 R2 업로드 완료:', imageUrls);
        return imageUrls;
      }
      
    } catch (error) {
      console.error('이미지 R2 업로드 오류:', error);
      throw error;
    }
  };

  // Instagram 링크에서 short_code 추출 함수 (릴스 + 포스트 지원)
  const extractShortCode = (link: string): string => {
    // 릴스: /reel/DMXH-JyTmwS/
    const reelMatch = link.match(/\/reel\/([A-Za-z0-9_-]+)/);
    if (reelMatch) {
      return reelMatch[1];
    }
    
    // 포스트: /p/DP-czk1jnOj/
    const postMatch = link.match(/\/p\/([A-Za-z0-9_-]+)/);
    if (postMatch) {
      return postMatch[1];
    }
    
    return '';
  };

  // 썸네일 미리보기 생성 함수
  const generateThumbnailPreview = (second: number) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const clampedSecond = Math.max(0, Math.min(second, video.duration));
    const prevTime = video.currentTime;
    video.currentTime = clampedSecond;
    const onSeeked = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const aspectRatio = video.videoWidth / video.videoHeight;
      canvas.width = 320;
      canvas.height = 320 / aspectRatio;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setThumbnailPreview(canvas.toDataURL('image/jpeg', 0.8));
      video.currentTime = prevTime;
      video.removeEventListener('seeked', onSeeked);
    };
    video.addEventListener('seeked', onSeeked);
  };

  // 영상에서 썸네일 추출 함수
  const extractThumbnail = async (videoFile: File, contentId?: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context를 가져올 수 없습니다.'));
        return;
      }

      video.onloadedmetadata = () => {
        // 사용자가 설정한 썸네일 초 지점으로 설정
        video.currentTime = thumbnailSecond;
      };

      video.onseeked = () => {
        // 캔버스 크기 설정 (16:9 비율 유지)
        const aspectRatio = video.videoWidth / video.videoHeight;
        canvas.width = 320;
        canvas.height = 320 / aspectRatio;
        
        // 비디오 프레임을 캔버스에 그리기
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // 캔버스를 Blob으로 변환
        canvas.toBlob((blob) => {
          if (blob) {
            // Blob을 File 객체로 변환
            const thumbnailFile = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
            
            // R2에 업로드 (contentId 전달)
            uploadThumbnailToStorage(thumbnailFile, contentId)
              .then(resolve)
              .catch(reject);
          } else {
            reject(new Error('썸네일 생성에 실패했습니다.'));
          }
        }, 'image/jpeg', 0.8);
      };

      video.onerror = () => {
        reject(new Error('비디오 로드에 실패했습니다.'));
      };

      // 비디오 소스 설정
      video.src = URL.createObjectURL(videoFile);
      video.load();
    });
  };

  // 썸네일을 R2에 업로드하는 함수
  const uploadThumbnailToStorage = async (thumbnailFile: File, contentId?: string, version?: string): Promise<string> => {
    try {
      if (contentId) {
        // contentId가 있으면 R2 업로드 사용
        const { uploadThumbnail } = await import('../../utils/r2Upload');
        return await uploadThumbnail(thumbnailFile, contentId, version);
      } else {
        // 임시 업로드 (contentId가 없을 때)
        const timestamp = Date.now();
        const fileName = `temp/thumbnails/${timestamp}_thumbnail.jpg`;
        const { uploadToR2, BUCKETS } = await import('../../utils/r2Upload');
        return await uploadToR2(thumbnailFile, fileName, BUCKETS.CONTENTS);
      }
    } catch (error) {
      console.error('썸네일 업로드 중 오류:', error);
      throw error;
    }
  };

  // 새로운 reel 생성 함수 수정 (seller_store.id 사용)
  const createNewReel = async (): Promise<number> => {
    if (!user) {
      throw new Error('사용자 정보를 가져올 수 없습니다.');
    }

    try {

      // seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        console.error('Seller Profile 정보를 찾을 수 없습니다:', profileError);
        throw new Error('판매자 프로필 정보를 찾을 수 없습니다. 먼저 판매자 등록을 완료해주세요.');
      }

      console.log('새로운 reel 생성 시작...');
      console.log('Seller Profile ID:', sellerProfile.id);
      
      // Instagram Reel 링크에서 short_code 추출
      const shortCode = extractShortCode(instagramReelLink);
      console.log('추출된 short_code:', shortCode);

      // ─── [NEW] selectedIgMedia에서 ig_contents_code 직접 사용 ───────────────
      // 기존: shortcode로 Graph API 재조회 → 이제 picker에서 선택 시 이미 media_id를 갖고 있음
      let igContentsCode: string | null = selectedIgMedia?.id ?? null;
      console.log('ig_contents_code (selectedIgMedia):', igContentsCode);

      /* ─── [COMMENTED OUT] 기존 shortcode 매칭 로직 ──────────────────────────
      try {
        const { data: brandData, error: brandError } = await supabase
          .from('share2dm_brands')
          .select('ig_account_id, ig_access_token')
          .eq('clozet_seller_id', sellerProfile.id)
          .single();

        if (brandError || !brandData) {
          console.warn('share2dm_brands 정보를 찾을 수 없습니다:', brandError);
        } else {
          const { ig_account_id, ig_access_token } = brandData;
          const mediaRes = await fetch(
            `https://graph.facebook.com/v21.0/${ig_account_id}/media?fields=id,shortcode&limit=50&access_token=${ig_access_token}`
          );
          const mediaData = await mediaRes.json();
          if (mediaData.data && Array.isArray(mediaData.data)) {
            const matched = mediaData.data.find((m: { id: string; shortcode: string }) => m.shortcode === shortCode);
            if (matched) {
              igContentsCode = matched.id;
            } else {
              console.warn('shortcode 매칭 실패 - 최근 50개 미디어에서 찾을 수 없음:', shortCode);
            }
          }
        }
      } catch (e) {
        console.warn('ig_contents_code 조회 실패 (무시):', e);
      }
      ─────────────────────────────────────────────────────────────────────── */

      // 새로운 content 레코드 생성 (creator_id를 seller_profile.id로 설정)
      const { data: contentData, error: contentCreateError } = await supabase
        .from('contents')
        .insert({
          creator_id: sellerProfile.id, // seller_profile.id 사용
          video_url: contentType === 'video' ? '' : null, // 비디오가 아니면 null
          thumbnail_url: '', // 나중에 업데이트
          thumbnail_second: thumbnailSecond,
          description: description || '새로운 콘텐츠',
          keywords: keywords.length > 0 ? keywords : [],
          view_count: 0,
          like_count: 0,
          comment_count: 0,
          short_code: shortCode, // short_code 추가
          ig_contents_code: igContentsCode, // DM 매핑용 Instagram media ID
          content_type: contentType, // 컨텐츠 타입 추가
          image_urls: contentType === 'image' ? [] : null // 이미지 타입이면 빈 배열, 아니면 null
        })
        .select('id')
        .single();

      if (contentCreateError) {
        console.error('content 생성 실패:', contentCreateError);
        throw contentCreateError;
      }

      const newContentId = contentData.id;
      console.log('새로운 content ID 생성 완료:', newContentId);
      return newContentId;
      
    } catch (error) {
      console.error('reel 생성 중 오류:', error);
      throw error;
    }
  };

  // 상태 추가 (import 후 state 선언 부분)
  const [isUploading, setIsUploading] = useState(false); // 업로드 중 상태 추가
  const navigate = useNavigate(); // import { useNavigate } from 'react-router-dom'; 추가 필요

  // 통합 업로드/수정 함수 (수정 모드에서는 변경된 부분만 업데이트)
  const handleUpload = async () => {
    // 컨텐츠 타입별 검증
    if (contentType === 'video') {
      // [NEW] selectedIgMedia 또는 videoUrl 둘 중 하나 있으면 통과
      if ((!videoUrl && !selectedIgMedia) || stickers.length === 0) {
        alert('영상과 스티커를 모두 추가해주세요.');
        return;
      }
    } else if (contentType === 'image') {
      // 수정 모드: 기존 이미지(imageUrls)가 있거나 새 이미지(imageFiles)가 있으면 OK
      const hasImages = isEditMode ? (imageUrls.length > 0 || imageFiles.length > 0) : imageFiles.length > 0;
      if (!hasImages || stickers.length === 0) {
        alert('이미지와 스티커를 모두 추가해주세요.');
        return;
      }
    }

    // D2C 스티커 URL 검증
    const d2cWithoutUrl = stickers.find(s => s.isD2c && (!s.externalUrl || !s.externalUrl.trim()));
    if (d2cWithoutUrl) {
      alert(`D2C가 설정된 스티커(${d2cWithoutUrl.product.product_name})에 자사몰 URL이 없습니다.`);
      return;
    }

    if (!instagramReelLink.trim()) {
      alert('Instagram 링크를 입력해주세요.');
      return;
    }

    if (!description.trim()) {
      alert('설명을 입력해주세요.');
      return;
    }

    if (keywords.length === 0) {
      alert('키워드를 최소 1개 이상 추가해주세요.');
      return;
    }

    if (isUploading) return;

    try {
      setIsUploading(true);

      // 수정 모드 vs 생성 모드
      let targetContentId: string | number;

      if (isEditMode) {
        // 수정 모드: 기존 contentId 사용 (새 레코드 생성 안 함)
        targetContentId = contentId;
        console.log('수정 모드 - 기존 content ID:', targetContentId);
      } else {
        // 생성 모드: 새 content 레코드 생성
        console.log('1단계: 새로운 content 생성');
        targetContentId = await createNewReel();
        console.log('생성된 content ID:', targetContentId);
      }

      let uploadedVideoUrl = null;
      let uploadedImageUrls = null;
      let thumbnailUrl = '';

      // 영상/이미지가 변경되었는지 감지
      const isNewVideo = videoUrl?.startsWith('blob:');
      const isNewImages = imageFiles.length > 0;

      if (contentType === 'video') {
        // ─── [NEW] Instagram picker로 선택한 경우: R2 업로드 스킵 ─────────────
        if (selectedIgMedia) {
          // video_url 저장 안 함 — Workers가 ig_contents_code로 on-demand 조회
          thumbnailUrl = selectedIgMedia.thumbnail_url ?? '';
          console.log('Instagram 선택 미디어 — R2 업로드 스킵, thumbnail:', thumbnailUrl);
        } else
        /* ─── [COMMENTED OUT] 기존 파일 업로드 → R2 영상 업로드 로직 ────────────
        if (isNewVideo) {
          console.log('영상 R2 업로드 시작 (변경됨)');
          if (!videoUrl) throw new Error('비디오 URL이 없습니다.');
          const response = await fetch(videoUrl);
          const videoBlob = await response.blob();
          const file = new File([videoBlob], 'video.mp4', { type: videoBlob.type });
          uploadedVideoUrl = await uploadVideoToR2(file, targetContentId.toString());
          console.log('영상 R2 업로드 완료:', uploadedVideoUrl);
          thumbnailUrl = await extractThumbnail(file, targetContentId.toString());
          console.log('썸네일 업로드 완료:', thumbnailUrl);
        } else
        ─────────────────────────────────────────────────────────────────────── */
        if (isNewVideo) {
          // 기존 로직 (파일 직접 업로드) — 현재 미사용, 하위호환 유지
          console.log('영상 R2 업로드 시작 (변경됨)');
          if (!videoUrl) throw new Error('비디오 URL이 없습니다.');
          const response = await fetch(videoUrl);
          const videoBlob = await response.blob();
          const file = new File([videoBlob], 'video.mp4', { type: videoBlob.type });
          uploadedVideoUrl = await uploadVideoToR2(file, targetContentId.toString());
          console.log('영상 R2 업로드 완료:', uploadedVideoUrl);
          thumbnailUrl = await extractThumbnail(file, targetContentId.toString());
          console.log('썸네일 업로드 완료:', thumbnailUrl);
        } else if (isEditMode && videoRef.current) {
          // 수정 모드에서 영상 미변경 — videoRef에서 thumbnailSecond 시점으로 썸네일 재추출
          console.log('영상 변경 없음 - 썸네일만 재추출');
          const version = Date.now().toString();
          thumbnailUrl = await new Promise<string>((resolve, reject) => {
            const video = videoRef.current!;
            const clampedSecond = Math.max(0, Math.min(thumbnailSecond, video.duration || thumbnailSecond));
            const prevTime = video.currentTime;
            video.currentTime = clampedSecond;
            const onSeeked = async () => {
              video.removeEventListener('seeked', onSeeked);
              try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) { reject(new Error('Canvas context 없음')); return; }
                const aspectRatio = video.videoWidth / video.videoHeight;
                canvas.width = 320;
                canvas.height = 320 / aspectRatio;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(async (blob) => {
                  if (!blob) { reject(new Error('썸네일 Blob 생성 실패')); return; }
                  const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
                  const url = await uploadThumbnailToStorage(file, targetContentId.toString(), version);
                  video.currentTime = prevTime;
                  resolve(url);
                }, 'image/jpeg', 0.8);
              } catch (e) {
                reject(e);
              }
            };
            video.addEventListener('seeked', onSeeked);
          });
          console.log('썸네일 재추출 완료:', thumbnailUrl);
        } else {
          console.log('영상 변경 없음 - R2 업로드 스킵');
        }
      } else if (contentType === 'image') {
        if (isNewImages) {
          // 새 이미지가 선택된 경우에만 R2 업로드
          console.log('이미지 R2 업로드 시작 (변경됨)');
          uploadedImageUrls = await uploadImagesToR2(imageFiles, targetContentId.toString());
          console.log('이미지 R2 업로드 완료:', uploadedImageUrls);
          thumbnailUrl = uploadedImageUrls[0];
        } else {
          console.log('이미지 변경 없음 - R2 업로드 스킵');
        }
      }

      // contents 테이블 업데이트 (수정된 메타데이터는 항상 업데이트)
      console.log('contents 테이블 업데이트');
      const shortCode = extractShortCode(instagramReelLink);
      const updateData: any = {
        description: description,
        keywords: keywords,
        short_code: shortCode,
        thumbnail_second: thumbnailSecond,
      };

      // 영상/이미지/썸네일은 변경된 경우에만 업데이트
      if (contentType === 'video' && uploadedVideoUrl) {
        updateData.video_url = uploadedVideoUrl;
      }
      if (thumbnailUrl) {
        updateData.thumbnail_url = thumbnailUrl;
      }
      if (contentType === 'image' && uploadedImageUrls) {
        updateData.image_urls = uploadedImageUrls;
      }

      const { error: contentUpdateError } = await supabase
        .from('contents')
        .update(updateData)
        .eq('id', targetContentId);

      if (contentUpdateError) {
        console.error('contents 테이블 업데이트 실패:', contentUpdateError);
        throw contentUpdateError;
      }

      // 스티커 정보 저장 (수정 모드에서는 기존 태그 삭제 후 새로 insert)
      console.log('스티커 정보 저장');
      // 크리에이터는 상품 태그 없이 저장
      if (!isCreator) {
        if (isEditMode) {
          const { error: deleteError } = await supabase
            .from('content_product_tags')
            .delete()
            .eq('contents_id', targetContentId);
          if (deleteError) throw deleteError;
        }

        const stickerData = stickers.map(sticker => ({
          contents_id: targetContentId,
          product_id: sticker.product.product_id,
          start_time: contentType === 'image' ? 0 : frameToTime(sticker.startFrame, videoFps),
          end_time: contentType === 'image' ? 1 : frameToTime(sticker.endFrame, videoFps),
          position_x: sticker.x,
          position_y: sticker.y,
          image_index: contentType === 'image' ? (sticker.imageIndex ?? 0) : 0,
          show_image: sticker.showImage ?? true,
          is_d2c: sticker.isD2c ?? false,
          external_url: sticker.isD2c ? sticker.externalUrl : null
        }));

        const { error: stickerError } = await supabase
          .from('content_product_tags')
          .insert(stickerData);
        if (stickerError) throw stickerError;
      }

      console.log('모든 처리 완료!');
      alert(isEditMode
        ? `콘텐츠가 성공적으로 수정되었습니다!`
        : `콘텐츠가 성공적으로 생성되었습니다! (ID: ${targetContentId})`
      );

      navigate(isCreator ? '/creator/contents' : '/seller/contents');

    } catch (error) {
      console.error(isEditMode ? '수정 실패:' : '업로드 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      alert(`${isEditMode ? '수정' : '업로드'}에 실패했습니다: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  // 볼륨 조절
  const handleVolumeChange = (v: number) => {
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
    console.log(`- 볼륨 조절: ${v}`);
  };
  // 프레임 이동
  const handleFrameMove = (i: number) => {
    setFrame(i);
    setSelectedBarId('main');
    console.log(`- 프레임 이동: ${i}`);
    
    // 영상의 재생 위치도 함께 변경
    if (videoRef.current && videoRef.current.duration) {
      const duration = videoRef.current.duration;
      const newTime = (i / (frameCount - 1)) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // 프레임바 클릭으로 영상 위치 변경
  const handleFrameBarClick = (e: React.MouseEvent) => {
    // 드래그 중일 때는 클릭 이벤트 무시
    if (isDraggingPlayhead) return;
    
    if (!videoRef.current || !videoRef.current.duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickRatio = clickX / rect.width;
    const newTime = clickRatio * videoRef.current.duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    // 프레임도 함께 업데이트
    const newFrame = Math.floor(clickRatio * (frameCount - 1));
    setFrame(newFrame);
    
    // 메인 프레임바 선택 상태로 변경
    setSelectedBarId('main');
    setLoopRange({start: 0, end: frameCount});
    
    console.log(`- 프레임바 클릭: ${newTime.toFixed(2)}초, 프레임: ${newFrame}`);
  };

  // 메인 프레임바 선택 (정보 출력용)
  const handleMainFrameBarSelect = () => {
    console.log(`- 메인 프레임바 선택 !`);
    setSelectedBarId('main');
    setLoopRange({start: 0, end: frameCount});
    if (stickers.length > 0) {
      console.log('모든 스티커');
      const sortedStickers = [...stickers].sort((a, b) => a.startFrame - b.startFrame);
      sortedStickers.forEach(sticker => {
        console.log(` {상품명: ${sticker.product.product_name}, 상품 가격: ${sticker.product.price}, 상대 위치: (${sticker.x.toFixed(2)}, ${sticker.y.toFixed(2)}), 프레임 구간: ${sticker.startFrame}~${sticker.endFrame}, 프레임바: ${sticker.barId}}`);
      });
    } else {
      console.log('등록된 스티커 없음');
    }
  };

  // 작대기 드래그 시작
  const handlePlayheadMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    setIsDraggingPlayhead(true);
    console.log('- 작대기 드래그 시작');
  };

  // 작대기 드래그 중
  const handlePlayheadDrag = (e: MouseEvent) => {
    if (!isDraggingPlayhead || !videoRef.current || !videoRef.current.duration) return;
    
    const frameBar = document.getElementById('main-frame-bar');
    if (!frameBar) return;
    
    const rect = frameBar.getBoundingClientRect();
    const dragX = e.clientX - rect.left;
    const dragRatio = Math.max(0, Math.min(1, dragX / rect.width));
    const newTime = dragRatio * videoRef.current.duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    // 프레임도 함께 업데이트
    const newFrame = Math.floor(dragRatio * (frameCount - 1));
    setFrame(newFrame);
    
    console.log(`- 작대기 드래그: ${newTime.toFixed(2)}초, 프레임: ${newFrame}`);
  };

  // 작대기 드래그 종료
  const handlePlayheadDragEnd = () => {
    setIsDraggingPlayhead(false);
    console.log('- 작대기 드래그 종료');
  };

  // 전역 마우스 이벤트 리스너
  useEffect(() => {
    if (isDraggingPlayhead) {
      window.addEventListener('mousemove', handlePlayheadDrag);
      window.addEventListener('mouseup', handlePlayheadDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handlePlayheadDrag);
        window.removeEventListener('mouseup', handlePlayheadDragEnd);
      };
    }
  }, [isDraggingPlayhead, frameCount]);

  return (
    <Box sx={styles.pageContainer}>
      <Box maxWidth={800} mx="auto">
        <Box sx={{ ...styles.paper, p: { xs: 2, sm: 4 }, mb: 4 }}>
          <Typography variant="h4" mb={3} align="center" sx={styles.title}>{isEditMode ? 'Edit Content' : 'Upload a reel'}</Typography>
          <Stack spacing={3} alignItems="center">
            <TextField 
              fullWidth 
              placeholder="Instagram Link (릴스: https://www.instagram.com/reel/abcd1234/ 또는 포스트: https://www.instagram.com/p/abcd1234/)"
              value={instagramReelLink}
              onChange={(e) => setInstagramReelLink(e.target.value)}
              sx={styles.input}
            />
            <TextField 
              fullWidth 
              multiline 
              minRows={3} 
              placeholder="Description" 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={styles.input}
            />
            <KeywordSelector keywords={keywords} setKeywords={setKeywords} />
            
            {/* 컨텐츠 타입 선택 탭 */}
            <Box sx={{ width: 300, mb: 2 }}>
              <Tabs
                value={contentType === 'video' ? 0 : 1}
                onChange={(_, newValue) => {
                  if (isEditMode) return; // 수정 모드에서는 타입 변경 불가
                  setContentType(newValue === 0 ? 'video' : 'image');
                  // 타입 변경 시 기존 데이터 초기화
                  if (newValue === 0) {
                    setImageFiles([]);
                    setImageUrls([]);
                    setCurrentImageIndex(0);
                  } else {
                    setVideoUrl(null);
                  }
                  setStickers([]); // 스티커도 초기화
                }}
                centered
                sx={{ 
                  '& .MuiTab-root': {
                    color: styles.text.secondary,
                    '&.Mui-selected': {
                      color: styles.text.primary
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: styles.text.primary
                  }
                }}
              >
                <Tab label="Video" />
                <Tab label="Images" />
              </Tabs>
            </Box>

            {/* 9:16 업로드/영상/이미지/오버레이 박스 */}
            <Box sx={{ width: 300, height: 533, aspectRatio: '9/16', bgcolor: isDarkMode ? '#404040' : '#e0e0e0', borderRadius: 3, mx: 'auto', my: 2, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* 컨텐츠 없을 때: 선택 버튼 */}
              {!videoUrl && imageUrls.length === 0 && (
                <Stack alignItems="center" spacing={2}>
                  {/* ─── [NEW] 비디오: Instagram 피드에서 선택 ─── */}
                  {contentType === 'video' ? (
                    <>
                      <Button
                        variant="text"
                        sx={{ ...styles.button.primary, fontWeight: 700, fontSize: 16 }}
                        onClick={fetchIgMedia}
                        disabled={igMediaLoading}
                      >
                        {igMediaLoading ? <CircularProgress size={20} /> : 'Instagram에서 선택'}
                      </Button>
                      <Typography variant="caption" color={styles.text.secondary}>
                        내 Instagram 릴스 중에서 선택
                      </Typography>
                    </>
                  ) : (
                    /* ─── 이미지: 기존 파일 업로드 유지 ─── */
                    <>
                      <Button
                        variant="text"
                        sx={{ ...styles.button.primary, fontWeight: 700, fontSize: 18 }}
                        onClick={() => imageInputRef.current?.click()}
                      >
                        Browse Images
                      </Button>
                      <Typography variant="caption" color={styles.text.secondary}>
                        Upload 1-10 image files
                      </Typography>
                      <Typography variant="caption" color={styles.text.secondary} sx={{ fontSize: '0.7rem', textAlign: 'center', mt: -1 }}>
                        이미지 높이 최대 17000픽셀
                      </Typography>
                    </>
                  )}
                </Stack>
              )}
              {/* [COMMENTED OUT] 기존 비디오 파일 input — Instagram picker로 대체됨 */}
              {/* <input type="file" hidden ref={fileInputRef} accept="video/*" onChange={handleFileChange} /> */}
              <input type="file" hidden ref={imageInputRef} accept="image/*" multiple onChange={handleImageChange} />
              
              {/* 영상 업로드 시: 영상 오버레이 */}
              {contentType === 'video' && videoUrl && (
                <Box sx={{ position: 'absolute', left: 0, top: 0, width: 300, height: 533, zIndex: 1 }}>
                  <video
                    ref={videoRef}
                    src={videoUrl ?? undefined}
                    crossOrigin="anonymous"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 3 }}
                    onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
                    controls
                  />
                  {/* 현재 시간에 맞는 스티커만 렌더링 */}
                  {visibleStickers.map(sticker => {
                    const videoWidth = 300;
                    const videoHeight = 533;
                    const k = 0.5 * videoWidth;
                    const stickerHeight = 0.14 * videoHeight;
                    return (
                      <Box
                        key={sticker.id}
                        sx={{
                          position: 'absolute',
                          left: sticker.x * videoWidth - k / 2,
                          top: sticker.y * videoHeight - stickerHeight / 2,
                          zIndex: 10,
                          cursor: selectedBarId === 'main' ? 'default' : 'grab',
                          width: k,
                          height: stickerHeight,
                          userSelect: 'none',
                          WebkitUserDrag: 'none',
                        }}
                        {...(selectedBarId !== 'main' ? { onMouseDown: (e: React.MouseEvent) => handleStickerDrag(sticker.id, e) } : {})}
                      >
                        <Sticker
                          image={sticker.product.main_images?.[0] || ''}
                          brand={sticker.product.product_name} // 키워드 대신 상품명
                          name={sticker.product.store_name} // 상품명 대신 store_name
                          price={sticker.product.price}
                          k={k}
                          height={stickerHeight}
                          showImage={sticker.showImage ?? true}
                          {...(selectedBarId !== 'main' ? { onRemove: () => handleRemoveSticker(sticker.id) } : {})}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
              
              {/* 이미지 업로드 시: 이미지 갤러리 */}
              {contentType === 'image' && imageUrls.length > 0 && (
                <Box 
                  ref={imageContainerRef}
                  sx={{ position: 'absolute', left: 0, top: 0, width: 300, height: 533, zIndex: 1 }}
                >
                  {/* 메인 이미지 표시 */}
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: '100%', 
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      // 이미지 클릭 시 다음 이미지로 전환
                      if (imageUrls.length > 1) {
                        setCurrentImageIndex((prev) => (prev + 1) % imageUrls.length);
                      }
                    }}
                  >
                    <img
                      src={imageUrls[currentImageIndex]}
                      alt={`Image ${currentImageIndex + 1}`}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain', 
                        borderRadius: 12 
                      }}
                    />
                    
                    {/* 이미지 인디케이터 */}
                    {imageUrls.length > 1 && (
                      <Box sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {currentImageIndex + 1} / {imageUrls.length}
                      </Box>
                    )}
                    
                    {/* 이미지 네비게이션 버튼 */}
                    {imageUrls.length > 1 && (
                      <>
                        <IconButton
                          sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex((prev) => prev === 0 ? imageUrls.length - 1 : prev - 1);
                          }}
                        >
                          ←
                        </IconButton>
                        <IconButton
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex((prev) => (prev + 1) % imageUrls.length);
                          }}
                        >
                          →
                        </IconButton>
                      </>
                    )}
                  </Box>
                  
                  {/* 현재 이미지에 해당하는 스티커만 표시 */}
                  {stickers
                    .filter(sticker => sticker.imageIndex === currentImageIndex)
                    .map(sticker => {
                      const imageWidth = 300;
                      const imageHeight = 533;
                      const k = 0.5 * imageWidth;
                      const stickerHeight = 0.14 * imageHeight;
                      return (
                        <Box
                          key={sticker.id}
                          sx={{
                            position: 'absolute',
                            left: sticker.x * imageWidth - k / 2,
                            top: sticker.y * imageHeight - stickerHeight / 2,
                            zIndex: 10,
                            cursor: 'grab',
                            width: k,
                            height: stickerHeight,
                            userSelect: 'none',
                            WebkitUserDrag: 'none',
                          }}
                          onMouseDown={(e: React.MouseEvent) => handleStickerDrag(sticker.id, e)}
                        >
                          <Sticker
                            image={sticker.product.main_images?.[0] || ''}
                            brand={sticker.product.product_name}
                            name={sticker.product.store_name}
                            price={sticker.product.price}
                            k={k}
                            height={stickerHeight}
                            showImage={sticker.showImage ?? true}
                            onRemove={() => handleRemoveSticker(sticker.id)}
                          />
                        </Box>
                      );
                    })}
                </Box>
              )}
            </Box>
            {/* 하단 컨트롤 - 비디오일 때는 프레임바, 이미지일 때는 간단한 컨트롤 */}
            {(videoUrl || imageUrls.length > 0) && (
              <>
                {contentType === 'video' && (
                  <>
                    {/* 프레임 정보 표시 (선택된 프레임바에 따라 다름) */}
                    <Box sx={{ width: 300, textAlign: 'center', mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: styles.text.secondary, fontFamily: 'monospace' }}>
                        {selectedBarId === 'main' 
                          ? formatFrameTime(currentFrameFromTime, videoFps)
                          : (() => {
                              const selectedBar = selectBars.find(b => b.id === selectedBarId);
                              if (selectedBar && videoRef.current) {
                                const startTime = (selectedBar.startPx / barWidth) * videoRef.current.duration;
                                const endTime = (selectedBar.endPx / barWidth) * videoRef.current.duration;
                                const startFrame = timeToFrame(startTime, videoFps);
                                const endFrame = timeToFrame(endTime, videoFps);
                                return formatFrameRange(startFrame, endFrame, videoFps);
                              }
                              return formatFrameTime(currentFrameFromTime, videoFps);
                            })()
                        }
                      </Typography>
                    </Box>
                  </>
                )}
                
                {contentType === 'image' && (
                  <>
                    {/* 이미지 정보 표시 */}
                    <Box sx={{ width: 300, textAlign: 'center', mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: styles.text.secondary }}>
                        {imageUrls.length}개 이미지 업로드됨
                      </Typography>
                    </Box>
                    
                    {/* 이미지 썸네일 관리 */}
                    {imageUrls.length > 1 && (
                      <Box sx={{ width: 300, mt: 2 }}>
                        <Typography variant="body2" sx={{ color: styles.text.secondary, mb: 1, textAlign: 'center' }}>
                          이미지 순서 관리 (드래그해서 순서 변경)
                        </Typography>
                        <Grid container spacing={1} sx={{ maxHeight: 200, overflowY: 'auto', p: 1 }}>
                          {imageUrls.map((url, index) => (
                            <Grid item xs={3} key={index}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  cursor: 'grab',
                                  border: currentImageIndex === index ? '2px solid #1976d2' : '1px solid #ddd',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  '&:active': { cursor: 'grabbing' }
                                }}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('text/plain', index.toString());
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                  const toIndex = index;
                                  moveImage(fromIndex, toIndex);
                                }}
                                onClick={() => setCurrentImageIndex(index)}
                              >
                                <img
                                  src={url}
                                  alt={`Thumbnail ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                {/* 순서 번호 */}
                                <Box sx={{
                                  position: 'absolute',
                                  top: 2,
                                  left: 2,
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  fontSize: 10,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 600
                                }}>
                                  {index + 1}
                                </Box>
                                {/* 스티커 개수 표시 */}
                                {(() => {
                                  const stickerCount = stickers.filter(s => s.imageIndex === index).length;
                                  return stickerCount > 0 ? (
                                    <Box sx={{
                                      position: 'absolute',
                                      bottom: 2,
                                      left: 2,
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      bgcolor: 'rgba(25,118,210,0.9)',
                                      color: 'white',
                                      fontSize: 9,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 600
                                    }}>
                                      {stickerCount}
                                    </Box>
                                  ) : null;
                                })()}
                                {/* 삭제 버튼 */}
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 16,
                                    height: 16,
                                    bgcolor: 'rgba(255,0,0,0.7)',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,0,0,0.9)' }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index);
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </>
                )}
            
                {/* 하단 아이콘들 - 컨텐츠 타입에 따라 다르게 표시 */}
                <Box sx={{ width: 300, display: 'flex', justifyContent: 'center', gap: 2, zIndex: 3, mt: 1 }}>
                  {contentType === 'video' && (
                    <>
                      <IconButton color="primary" size="large" onClick={e => setVolumeAnchor(e.currentTarget)}>
                        <VolumeUpIcon fontSize="large" />
                      </IconButton>
                      <Popover
                        open={Boolean(volumeAnchor)}
                        anchorEl={volumeAnchor}
                        onClose={() => setVolumeAnchor(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        PaperProps={{ sx: { p: 2 } }}
                      >
                        <Box sx={{ width: 120 }}>
                          <Slider
                            min={0}
                            max={1}
                            step={0.01}
                            value={volume}
                            onChange={(_, v) => handleVolumeChange(v as number)}
                          />
                        </Box>
                      </Popover>
                      <IconButton color="primary" size="large" onClick={handlePlayPause}>
                        {isPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
                      </IconButton>
                      {/* + 아이콘(재생버튼 우측) 클릭 시 상품 선택 Dialog 오픈 */}
                      <IconButton 
                        color="primary" 
                        size="large" 
                        onClick={() => { setProductDialogOpen(true); }}
                        disabled={selectedBarId === 'main'} // 메인 프레임바일 때 비활성화
                        sx={{ 
                          opacity: selectedBarId === 'main' ? 0.5 : 1,
                          cursor: selectedBarId === 'main' ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <AddIcon fontSize="large" />
                      </IconButton>
                    </>
                  )}
                  
                  {contentType === 'image' && (
                    <>
                      {/* 이미지 모드에서는 스티커 추가 버튼만 표시 */}
                      <IconButton 
                        color="primary" 
                        size="large" 
                        onClick={() => { setProductDialogOpen(true); }}
                        sx={{ fontSize: 18, fontWeight: 600 }}
                      >
                        <AddIcon fontSize="large" />
                      </IconButton>
                    </>
                  )}
                </Box>
                {/* 프레임바 UI (비디오 모드에서만 표시) */}
                {contentType === 'video' && (
                  <Box sx={{ width: 600, zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'transparent', mt: 2 }}>
                  {/* 기본 프레임바 (썸네일 시퀀스) */}
                  <Box
                    id="main-frame-bar" // 작대기 드래그를 위한 ID 추가
                    sx={{
                      width: barWidth,
                      height: 56,
                      overflow: 'hidden',
                      mb: 2,
                      bgcolor: '#fff',
                      boxShadow: 2,
                      display: 'flex',
                      alignItems: 'center',
                      p: 0,
                      border: selectedBarId === 'main' ? '2.5px solid #1976d2' : 'none',
                      position: 'relative', // 상대 위치 설정
                    }}
                    onClick={handleFrameBarClick}
                    onDoubleClick={handleMainFrameBarSelect}
                    onMouseDown={handlePlayheadMouseDown} // 작대기 드래그 시작 핸들러 연결
                  >
                    {thumbs.length === frameCount ? thumbs.map((src, i) => (
                      <Box key={i} sx={{ flex: 1, height: 56, overflow: 'hidden', border: 'none', cursor: 'pointer' }}
                        onClick={() => handleFrameMove(i)}
                      >
                        <img src={src} alt={`thumb${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )) : Array.from({length: frameCount}).map((_, i) => (
                      <Box key={i} sx={{ flex: 1, height: 56, overflow: 'hidden', bgcolor: '#eee' }} />
                    ))}
                    
                    {/* 현재 재생 위치 작대기 - 항상 표시 */}
                    {videoRef.current && videoRef.current.duration && currentTime >= 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${Math.min(100, Math.max(0, (currentTime / (videoRef.current?.duration || 1)) * 100))}%`,
                          top: 0,
                          width: 3,
                          height: '100%',
                          bgcolor: '#ff4444',
                          zIndex: 10,
                          borderRadius: 1,
                          boxShadow: '0 0 4px rgba(255, 68, 68, 0.6)',
                          transform: 'translateX(-50%)', // 중앙 정렬
                          cursor: isDraggingPlayhead ? 'grabbing' : 'grab', // 드래그 커서
                          pointerEvents: 'auto', // 항상 클릭 가능하도록
                        }}
                        onMouseDown={handlePlayheadMouseDown}
                      />
                    )}
                  </Box>

                  {/* 여러 개의 영역 선택 바 (노란색, 블러, 핸들) — 크리에이터 제외 */}
                  {!isCreator && selectBars.map(bar => {
                    const startRatio = bar.startPx / barWidth;
                    const endRatio = bar.endPx / barWidth;
                    const startFrame = Math.floor(startRatio * frameCount);
                    const endFrame = Math.ceil(endRatio * frameCount);
                    return (
                      <Box
                        key={bar.id}
                        sx={{
                          width: '100%',
                          height: 56,
                          position: 'relative',
                          my: 1,
                          overflow: 'hidden',
                          bgcolor: '#fff',
                          boxShadow: 2,
                          display: 'flex',
                          alignItems: 'center',
                          border: selectedBarId === bar.id ? '2.5px solid #1976d2' : 'none',
                        }}
                        onClick={() => {
                          console.log(`- 프레임바 ${bar.id} 선택 !`);
                          setSelectedBarId(bar.id);
                          setLoopRange({start: startFrame, end: endFrame});
                          
                          // 프레임바의 시작 구간으로 영상 위치 즉시 이동
                          const barObj = selectBars.find(b => b.id === bar.id);
                          if (barObj && videoRef.current) {
                            const duration = videoRef.current.duration;
                            const barWidth = 500;
                            const startTime = (barObj.startPx / barWidth) * duration;
                            const startFrameNum = timeToFrame(startTime, videoFps);
                            
                            // 비디오 위치 즉시 업데이트
                            videoRef.current.currentTime = startTime;
                            setCurrentTime(startTime);
                            
                            // 썸네일 프레임바도 동기화
                            const thumbnailIndex = Math.floor((startTime / duration) * frameCount);
                            setFrame(thumbnailIndex);
                            
                            console.log(`- 프레임바 ${bar.id} 선택: 프레임 ${startFrameNum} (${startTime.toFixed(3)}초)로 이동`);
                          }
                          // 현재 포함된 스티커(프레임바에 속한 모든 스티커) 출력
                          const included = stickers.filter(sticker => sticker.barId === bar.id);
                          if (included.length > 0) {
                            console.log('현재 포함된 스티커');
                            included.forEach(sticker => {
                              console.log(` {상품명: ${sticker.product.product_name}, 상품 가격: ${sticker.product.price}, 상대 위치: (${sticker.x.toFixed(2)}, ${sticker.y.toFixed(2)}), 프레임 구간: ${sticker.startFrame}~${sticker.endFrame}}`);
                            });
                          } else {
                            console.log('현재 포함된 스티커 없음');
                          }
                        }}
                      >
                        {/* 좌측 재생 버튼 */}
                        <IconButton sx={{ mr: 1 }} color="primary" onClick={() => handlePlayBar(bar)}>
                          <PlayArrowIcon />
                        </IconButton>
                        {/* 프레임바 UI: 기존 코드 절대 변경 금지 */}
                        <Box sx={{ flex: 1, position: 'relative', height: 56 }}>
                          {/* 기존 SelectBar Box 코드 시작 */}
                          {/* 프레임 썸네일 시퀀스 (기본 프레임바와 동일, 클릭 불가) */}
                          <Box
                            sx={{ width: '100%', height: '100%', display: 'flex', position: 'absolute', left: 0, top: 0, zIndex: 0 }}
                            onClick={() => {
                              setSelectedBarId(bar.id);
                              setLoopRange({start: startFrame, end: endFrame});
                              
                              // 프레임바 시작 위치로 즉시 이동
                              const barObj = selectBars.find(b => b.id === bar.id);
                              if (barObj && videoRef.current) {
                                const duration = videoRef.current.duration;
                                const barWidth = 500;
                                const startTime = (barObj.startPx / barWidth) * duration;
                                const startFrameNum = timeToFrame(startTime, videoFps);
                                
                                videoRef.current.currentTime = startTime;
                                setCurrentTime(startTime);
                                
                                const thumbnailIndex = Math.floor((startTime / duration) * frameCount);
                                setFrame(thumbnailIndex);
                                
                                console.log(`- 프레임바 ${bar.id} 내부 클릭: 프레임 ${startFrameNum} (${startTime.toFixed(3)}초)로 이동`);
                              }
                            }}
                          >
                            {thumbs.length === frameCount ? thumbs.map((src, i) => (
                              <Box key={i} sx={{ flex: 1, height: '100%', overflow: 'hidden', border: 'none', cursor: 'default' }}>
                                <img src={src} alt={`thumb${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </Box>
                            )) : Array.from({length: frameCount}).map((_, i) => (
                              <Box key={i} sx={{ flex: 1, height: '100%', overflow: 'hidden', bgcolor: '#eee' }} />
                            ))}
                          </Box>
                          {/* 블러 처리: 선택 영역 외 */}
                          <Box sx={{ position: 'absolute', left: 0, top: 0, width: `${startRatio * 100}%`, height: '100%', bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(2.5px)', zIndex: 1 }} />
                          <Box sx={{ position: 'absolute', left: `${endRatio * 100}%`, top: 0, width: `${(1-endRatio) * 100}%`, height: '100%', bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(2.5px)', zIndex: 1 }} />
                          {/* 선택 영역: 덩어리 전체 드래그 이동 */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: `${startRatio * 100}%`,
                              top: 0,
                              width: `${(endRatio-startRatio)*100}%`,
                              height: '100%',
                              opacity: 0.7,
                              zIndex: 2,
                              cursor: 'grab',
                            }}
                            onMouseDown={e => {
                              e.stopPropagation();
                              const startX = e.clientX;
                              const startStart = bar.startPx;
                              const startEnd = bar.endPx;
                              const onMove = (ev: MouseEvent) => {
                                const dx = ev.clientX - startX;
                                let nextStart = startStart + dx;
                                let nextEnd = startEnd + dx;
                                if (nextStart < 0) {
                                  nextEnd += -nextStart;
                                  nextStart = 0;
                                }
                                if (nextEnd > barWidth) {
                                  nextStart -= (nextEnd - barWidth);
                                  nextEnd = barWidth;
                                }
                                if (nextEnd - nextStart < 20) return;
                                handleBarChange(bar.id, 'startPx', nextStart, true);
                                handleBarChange(bar.id, 'endPx', nextEnd, true);
                              };
                              const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                              window.addEventListener('mousemove', onMove);
                              window.addEventListener('mouseup', onUp);
                            }}
                          >
                            {/* 좌측 드래그 핫존 */}
                            <Box sx={{ position: 'absolute', left: 0, top: 0, width: 6, height: '100%', cursor: 'ew-resize', zIndex: 3, bgcolor: 'rgba(0, 0, 0, 1)' }}
                              onMouseDown={e => {
                                e.stopPropagation();
                                const startX = e.clientX;
                                const startVal = bar.startPx;
                                const onMove = (ev: MouseEvent) => {
                                  const dx = ev.clientX - startX;
                                  let next = startVal + dx;
                                  if (next > bar.endPx - 20) next = bar.endPx - 20;
                                  if (next < 0) next = 0;
                                  handleBarChange(bar.id, 'startPx', next, false);
                                };
                                const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                                window.addEventListener('mousemove', onMove);
                                window.addEventListener('mouseup', onUp);
                              }}
                            />
                            {/* 우측 드래그 핫존 */}
                            <Box sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'ew-resize', zIndex: 3, bgcolor: 'rgba(0, 0, 0, 1)' }}
                              onMouseDown={e => {
                                e.stopPropagation();
                                const startX = e.clientX;
                                const endVal = bar.endPx;
                                const onMove = (ev: MouseEvent) => {
                                  const dx = ev.clientX - startX;
                                  let next = endVal + dx;
                                  if (next < bar.startPx + 20) next = bar.startPx + 20;
                                  if (next > barWidth) next = barWidth;
                                  handleBarChange(bar.id, 'endPx', next, false);
                                };
                                const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                                window.addEventListener('mousemove', onMove);
                                window.addEventListener('mouseup', onUp);
                              }}
                            />
                          </Box>
                        </Box>
                        {/* 우측 삭제 버튼 */}
                        <IconButton sx={{ ml: 1 }} color="error" onClick={() => handleDeleteBar(bar.id)}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    );
                  })}
                  {/* + 아이콘 (프레임바 전체 하단 중앙) */}
                  {!isCreator && (
                  <IconButton sx={{ mt: 3, bgcolor: '#fff', border: '2px solid #ffb300', color: '#ffb300', zIndex: 10, width: 56, height: 56, borderRadius: '50%', boxShadow: 2 }} onClick={handleAddSelectBar}>
                    <AddCircleIcon fontSize="large" />
                  </IconButton>
                  )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Box>
        
        {/* 상품 선택 Dialog — 크리에이터 제외 */}
        {!isCreator && <Dialog
          open={productDialogOpen} 
          onClose={() => {
            setProductDialogOpen(false);
            setProductSearchTerm(''); // 검색어 초기화
          }} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: styles.paper }}
        >
          <DialogTitle sx={{ color: styles.text.primary }}>상품 선택</DialogTitle>
          <DialogContent>
            {/* 검색 입력 필드 추가 */}
            <TextField
              fullWidth
              placeholder="상품명 또는 상점명으로 검색..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              sx={{ ...styles.input, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ mr: 1, color: styles.text.secondary }}>
                    🔍
                  </Box>
                ),
              }}
            />

            {/* 스티커 이미지 표시 여부 체크박스 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={stickerShowImage}
                  onChange={(e) => setStickerShowImage(e.target.checked)}
                  sx={{ color: styles.text.secondary }}
                />
              }
              label="스티커에 제품 이미지 표시"
              sx={{ mb: 2, color: styles.text.primary }}
            />

            {loadingProducts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">검색된 상품이 없습니다.</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  상품 이름 또는 상점 이름을 다시 확인해주세요.
                </Typography>
              </Box>
            ) : (
              <List>
                {filteredProducts.map(product => (
                  <ListItem button key={product.product_id} onClick={() => handleSelectProduct(product)}>
                    <ListItemAvatar>
                      <Avatar 
                        src={product.main_images?.[0] || ''} 
                        alt={product.product_name}
                        sx={{ width: 56, height: 56 }}
                      />
                    </ListItemAvatar>
                    <ListItemText 
                      primary={product.product_name} // 상품명을 primary로
                      secondary={product.store_name} // 상점명을 secondary로
                    />
                    <Typography variant="body1" fontWeight={600}>
                      {product.price.toLocaleString()}원
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProductDialogOpen(false)} sx={{ color: styles.text.secondary }}>{t("common.cancel")}</Button>
          </DialogActions>
        </Dialog>}

        {/* 썸네일 초 설정 (비디오 모드에서만) */}
        {contentType === 'video' && videoUrl && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 2 }}>
            <Typography variant="subtitle2" sx={{ color: styles.text.secondary }}>
              썸네일 추출 시점 (초)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                type="number"
                size="small"
                value={thumbnailSecondInput}
                onChange={(e) => {
                  setThumbnailSecondInput(e.target.value);
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) setThumbnailSecond(Math.max(0, val));
                }}
                inputProps={{ step: 0.1, min: 0, max: videoRef.current?.duration || 999 }}
                sx={{ width: 120, ...styles.input }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => generateThumbnailPreview(thumbnailSecond)}
              >
                미리보기
              </Button>
            </Box>
            {thumbnailPreview && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: styles.text.secondary, mb: 1, display: 'block' }}>
                  {thumbnailSecond}초 썸네일 미리보기
                </Typography>
                <img
                  src={thumbnailPreview}
                  alt="썸네일 미리보기"
                  style={{ maxWidth: 200, borderRadius: 8, border: '1px solid #ddd' }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* 업로드/수정 버튼 */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={
              (contentType === 'video' && (!videoUrl || stickers.length === 0)) ||
              (contentType === 'image' && ((isEditMode ? imageUrls.length === 0 : imageFiles.length === 0) || stickers.length === 0)) ||
              !instagramReelLink.trim() || !description.trim() || keywords.length === 0 || isUploading
            }
            sx={{ ...styles.button.primary, fontWeight: 700, fontSize: 18, px: 4, py: 1.2 }}
          >
            {isUploading ? (isEditMode ? '수정 중...' : '업로드 중...') :
             contentType === 'video' && !videoUrl ? '영상을 먼저 업로드하세요' :
             contentType === 'image' && (isEditMode ? imageUrls.length === 0 : imageFiles.length === 0) ? '이미지를 먼저 업로드하세요' :
             !instagramReelLink.trim() ? 'Instagram 링크를 입력해주세요' :
             !description.trim() ? '설명을 입력해주세요' :
             keywords.length === 0 ? '키워드를 추가해주세요' :
             stickers.length === 0 ? '스티커를 추가해주세요' :
             isEditMode ? '수정 사항 저장' : `${contentType === 'video' ? '영상' : '이미지'} 및 스티커 정보 저장`}
          </Button>
        </Box>
      </Box>

      {/* ─── [NEW] Instagram 피드 Picker Dialog ─────────────────────────────── */}
      <Dialog open={showIgPicker} onClose={() => setShowIgPicker(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Instagram 릴스 선택
          <IconButton onClick={() => setShowIgPicker(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {igMediaLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : igMediaList.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              불러온 릴스가 없습니다.
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {igMediaList.map(item => (
                <Grid item xs={4} key={item.id}>
                  <Box
                    sx={{ position: 'relative', cursor: 'pointer', borderRadius: 1, overflow: 'hidden', aspectRatio: '9/16', bgcolor: '#222' }}
                    onClick={() => handleSelectIgMedia(item)}
                  >
                    <img
                      src={item.thumbnail_url ?? item.media_url}
                      alt={item.shortcode}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box sx={{ position: 'absolute', bottom: 4, right: 4 }}>
                      <PlayArrowIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {/* ─────────────────────────────────────────────────────────────────────── */}
    </Box>
  );
} 